import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAnalytics, useAdminCoupons, useAdminMerchants, useAppClients, useAdminCashbackPools, useAffiliateFeeds, useCouponSubmissions, useAuditLogs, useAuditStats, useAdminUsers, useRoleCatalogue, fetchUserRoles, assignUserRole, revokeUserRole, useSystemHealth, useWebhooks, bulkUserAction, startImpersonation, useOnboardingFunnel, useEmailHealth, useCohorts, useFeatureFlags, previewBroadcast, sendBroadcast, useBroadcasts, saveBroadcastDraft, updateBroadcast, cancelBroadcast, runBroadcastNow, deleteBroadcast, useSettings, testIntegration, useBanners, useBlog, useRevenue, useTierLifecycle, usePaymentSettings, updatePaymentSettings, clearPaymentOverride, testPaymentConnection, useMailSettings, updateMailSettings, clearMailOverride, sendMailTest, usePendingMerchants, fetchMerchantScope, saveMerchantScope } from '../../hooks/useAdmin';
import { StatCard, PageHeader, LoadingPage, ConfirmModal, EmptyState, useConfirm } from '../../components/common/UI';
import { useToast } from '../../components/common/Toast';
import MerchantLogo from '../../components/common/MerchantLogo';
import FeatureRoadmap from './FeatureRoadmap';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * Map raw webhook / HTTP error strings to user-readable summaries.
 * Keeps the original message available behind a "Show technical details"
 * disclosure so platform operators aren't deprived of the underlying cause.
 */
function friendlyWebhookError(raw) {
  if (!raw) return '';
  const lower = String(raw).toLowerCase();
  if (lower.includes('could not resolve host'))   return 'The webhook URL\'s domain is not reachable — check the host name and DNS.';
  if (lower.includes('connection refused'))       return 'The receiving server refused the connection. The service may be down or the port wrong.';
  if (lower.includes('connection timed out') ||
      lower.includes('operation timed out'))      return 'The receiving server didn\'t respond in time. Check that it\'s running and reachable from the public internet.';
  if (lower.includes('ssl certificate') ||
      lower.includes('certificate verify'))       return 'The receiver\'s SSL certificate could not be verified — it may be expired or self-signed.';
  if (lower.match(/http (4\d\d)/) || /^4\d\d /.test(lower))
                                                  return 'The receiver rejected the payload (4xx response). Check the secret + expected payload shape.';
  if (lower.match(/http (5\d\d)/) || /^5\d\d /.test(lower))
                                                  return 'The receiver returned a server error (5xx). Their service is broken or overloaded.';
  if (lower.includes('curl error'))               return 'Could not reach the webhook URL. Check the host, port, and that the server is publicly accessible.';
  // Keep the raw message if we don't have a friendlier mapping.
  return raw;
}

// Icons are bootstrap-icons classes (a consistent vector set already bundled
// as a dependency) rather than emojis — Ananya 06-30 #13 flagged the emoji
// icons as visually informal + inconsistent across platforms/resolutions.
// Rendered via <i className={icon}/> so they scale cleanly and inherit color
// for theme adaptability.
const NAV = [
  { id: 'overview',    label: 'Overview',         icon: 'bi bi-bar-chart-line' },
  { id: 'coupons',     label: 'Coupons',          icon: 'bi bi-tag' },
  { id: 'submissions', label: 'Submissions',      icon: 'bi bi-inbox' },
  { id: 'affiliate',   label: 'Affiliate Feeds',  icon: 'bi bi-link-45deg' },
  { id: 'merchants',   label: 'Merchants',        icon: 'bi bi-shop' },
  { id: 'cashback',    label: 'Cashback Pools',   icon: 'bi bi-cash-coin' },
  { id: 'apps',        label: 'App Clients',      icon: 'bi bi-plug' },
  { id: 'users',       label: 'Users',            icon: 'bi bi-people' },
  { id: 'fraud',       label: 'Fraud Log',        icon: 'bi bi-shield-exclamation' },
  { id: 'audit',       label: 'Audit Log',        icon: 'bi bi-clipboard-data' },
  { id: 'roles',       label: 'Roles',            icon: 'bi bi-shield-lock' },
  { id: 'webhooks',    label: 'Webhooks',         icon: 'bi bi-diagram-3' },
  { id: 'flags',       label: 'Feature Flags',    icon: 'bi bi-flag' },
  { id: 'broadcasts',  label: 'Broadcasts',       icon: 'bi bi-megaphone' },
  { id: 'funnel',      label: 'Funnel',           icon: 'bi bi-funnel' },
  { id: 'email',       label: 'Email Health',     icon: 'bi bi-envelope-check' },
  { id: 'cohorts',     label: 'Cohorts',          icon: 'bi bi-graph-up' },
  { id: 'revenue',     label: 'Revenue',          icon: 'bi bi-currency-dollar' },
  { id: 'tiers',       label: 'Tier Lifecycle',   icon: 'bi bi-trophy' },
  { id: 'banners',     label: 'Banners',          icon: 'bi bi-images' },
  { id: 'blog',        label: 'Blog',             icon: 'bi bi-pencil-square' },
  { id: 'settings',    label: 'Settings',         icon: 'bi bi-gear' },
  { id: 'payments',    label: 'Payment Settings', icon: 'bi bi-credit-card' },
  { id: 'mail',        label: 'Mail Settings',    icon: 'bi bi-envelope-gear' },
  { id: 'export',      label: 'Data Export',      icon: 'bi bi-box-arrow-up' },
  { id: 'health',      label: 'System Health',    icon: 'bi bi-heart-pulse' },
  { id: 'roadmap',     label: 'Roadmap',          icon: 'bi bi-signpost-split' },
];

/* ============================= Layout ============================= */

function AdminLayout({ children, active, setActive }) {
  const { user } = useAuth();
  return (
    <div className="portal">
      <aside className="portal-aside">
        <div className="portal-aside-top">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div className="portal-aside-brand">
              <span style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--brand-gradient)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800 }}>P</span>
              <span>PerkVora</span>
            </div>
            <p className="portal-aside-label">Super Admin</p>
          </Link>
        </div>

        <nav className="portal-aside-nav">
          {NAV.map(item => (
            <button key={item.id} onClick={() => setActive(item.id)}
              className={`portal-nav-item ${active === item.id ? 'active' : ''}`}
              title={item.label}
            >
              <span className="portal-nav-icon"><i className={item.icon} /></span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="portal-aside-footer">
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: 10, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {user.name?.[0]?.toUpperCase() || 'A'}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name || 'Admin'}</p>
                <p style={{ fontSize: 11, color: '#7A7F9E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</p>
              </div>
            </div>
          )}
          <Link to="/" className="portal-nav-item">
            <span className="portal-nav-icon"><i className="bi bi-arrow-left" /></span>
            <span>Back to site</span>
          </Link>
        </div>
      </aside>

      <main className="portal-main">{children}</main>
    </div>
  );
}

/* ============================= Overview ============================= */

function OverviewTab() {
  // These were declared with useState but no setter and rendered as styled
  // text, so the range looked like a control and could never be changed.
  const [from, setFrom] = useState(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [to,   setTo]   = useState(() => new Date().toISOString().slice(0, 10));
  const { overview, loading } = useAdminAnalytics(from, to);

  // Only block the whole page on the FIRST load. Re-rendering LoadingPage on
  // every range change would unmount the inputs mid-edit.
  if (loading && !overview) return <LoadingPage />;

  const o = overview || {};
  const spanDays = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000));
  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={`${spanDays} day${spanDays === 1 ? '' : 's'} to ${to}`}
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#fff', padding: '6px 12px', borderRadius: 10, border: '1px solid #E4E7F0', fontSize: 13, color: '#4A5568' }}>
            <i className="bi bi-calendar3" />
            <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)}
                   aria-label="Start date"
                   style={{ border: 'none', background: 'transparent', font: 'inherit', color: 'inherit', padding: 0 }} />
            <span>→</span>
            <input type="date" value={to} min={from} max={new Date().toISOString().slice(0, 10)}
                   onChange={e => setTo(e.target.value)} aria-label="End date"
                   style={{ border: 'none', background: 'transparent', font: 'inherit', color: 'inherit', padding: 0 }} />
            {loading && <span style={{ fontSize: 11, color: '#8B94A8' }}>updating…</span>}
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard gradient label="Total Redemptions"   value={(o.total_redemptions ?? 0).toLocaleString()} icon={<i className="bi bi-ticket-perforated" />} />
        <StatCard         label="Commission Earned"   value={`$${Number(o.total_commission ?? 0).toFixed(2)}`} icon={<i className="bi bi-cash-stack" />} iconBg="linear-gradient(135deg,#0F9D6E,#059669)" />
        <StatCard         label="Impressions"         value={(o.total_impressions ?? 0).toLocaleString()} icon={<i className="bi bi-eye" />} iconBg="linear-gradient(135deg,#F59E0B,#D97706)" />
        {/* Live count, deliberately not scoped to the date range above: it is
            "how many coupons are active right now", which is why it can differ
            from the Coupons page total (that counts every status). */}
        <StatCard         label="Active Coupons (now)" value={(o.active_coupons ?? 0).toLocaleString()} icon={<i className="bi bi-tag" />} iconBg="linear-gradient(135deg,#8B5CF6,#6B2FD9)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div className="ds-card">
          <div className="ds-card-head">
            <span className="ds-card-label">Active Merchants</span>
            <span className="ds-card-icon"><i className="bi bi-shop" /></span>
          </div>
          <p className="ds-card-value">{(o.active_merchants ?? 0).toLocaleString()}</p>
        </div>
        <div className="ds-card">
          <div className="ds-card-head">
            <span className="ds-card-label">Total Clicks</span>
            <span className="ds-card-icon"><i className="bi bi-mouse" /></span>
          </div>
          <p className="ds-card-value">{(o.total_clicks ?? 0).toLocaleString()}</p>
        </div>
        <div className="ds-card">
          <div className="ds-card-head">
            <span className="ds-card-label">Avg. Conversion</span>
            <span className="ds-card-icon"><i className="bi bi-bullseye" /></span>
          </div>
          <p className="ds-card-value">{o.total_impressions ? `${((o.total_redemptions / o.total_impressions) * 100).toFixed(2)}%` : '—'}</p>
        </div>
        <div className="ds-card">
          <div className="ds-card-head">
            <span className="ds-card-label">Avg. Order Value</span>
            <span className="ds-card-icon"><i className="bi bi-currency-dollar" /></span>
          </div>
          <p className="ds-card-value">${Number(o.avg_order_value ?? 0).toFixed(2)}</p>
        </div>
      </div>
    </>
  );
}

/* ============================= Coupons ============================= */

function CouponsTab() {
  const { coupons, loading, fetchCoupons, deleteCoupon, setCouponActive } = useAdminCoupons();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [showImport, setShowImport] = useState(false);

  // Debounce the search → backend so typing "nike" fires one fetch
  // (after the user pauses) instead of one per character. Also keeps
  // the existing loader from flashing on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchCoupons({ search: search || undefined, status: statusFilter || undefined });
    }, 300);
    return () => clearTimeout(t);
  }, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && coupons.length === 0) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="Coupons"
        subtitle={`${coupons.length} coupons total`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setShowImport(true)}><i className="bi bi-download" /> Import CSV</button>
            <button className="btn btn-primary"   onClick={() => setShowForm(true)}>+ New Coupon</button>
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8B94A8', fontSize: 14 }}><i className="bi bi-search" /></span>
          <input className="form-input" style={{ paddingLeft: 38 }} placeholder="Search coupons…" value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input form-select" style={{ width: 'auto', padding: '10px 34px 10px 14px' }}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="ds-panel">
        <div className="ds-panel-body-flush" style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead>
              <tr><th>Coupon</th><th>Merchant</th><th>Discount</th><th style={{ textAlign: 'right' }}>Used</th><th>Expires</th><th>Status</th><th style={{ width: 140 }}>Actions</th></tr>
            </thead>
            <tbody>
              {coupons.length === 0 ? (
                <tr><td colSpan={7}>
                  <EmptyState icon={<i className="bi bi-tags" />} title="No coupons yet" subtitle="Create your first coupon to get started." />
                </td></tr>
              ) : coupons.map(c => (
                <tr key={c.id}>
                  <td>
                    <p style={{ fontWeight: 600, color: '#0F1225' }}>{c.title}</p>
                    <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>#{c.id}</p>
                  </td>
                  <td style={{ fontSize: 13 }}>{c.merchant?.name || <span style={{ color: '#8B94A8' }}>—</span>}</td>
                  <td><span className="badge badge-gradient">{c.discount_value}{c.discount_type === 'percent' ? '%' : c.discount_type === 'fixed' ? '$' : ''} OFF</span></td>
                  <td style={{ textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{c.used_count ?? 0}{c.max_uses ? `/${c.max_uses}` : ''}</td>
                  <td style={{ fontSize: 12, color: '#8B94A8' }}>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                  <td><span className={`badge ${c.is_active ? 'badge-green' : 'badge-red'}`}>{c.is_active ? '● Active' : '● Inactive'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="ds-inline-btn" onClick={() => setEditing(c)}>Edit</button>
                      {/* An approved merchant coupon is a live offer someone
                          else owns. Deactivating pulls it from the site and
                          can be undone; deleting cannot, so it is not offered
                          (the API refuses it too). */}
                      {c.source === 'user_submitted' && c.submission_status === 'approved' ? (
                        <button
                          className="ds-inline-btn"
                          title={c.is_active ? 'Take this coupon off the site' : 'Put this coupon back on the site'}
                          onClick={() => setCouponActive(c.id, !c.is_active)}
                        >
                          {c.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      ) : (
                        <button className="ds-inline-btn danger" onClick={() => setDeleteId(c.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!deleteId}
        title="Delete Coupon"
        message="This coupon will be permanently deleted. This action cannot be undone."
        danger confirmLabel="Delete"
        onConfirm={async () => { await deleteCoupon(deleteId); setDeleteId(null); }}
        onCancel={() => setDeleteId(null)}
      />
      {showForm && <CouponFormModal onClose={() => { setShowForm(false); fetchCoupons(); }} />}
      {editing  && <CouponFormModal coupon={editing} onClose={() => { setEditing(null); fetchCoupons(); }} />}
      {showImport && <BulkImportCouponsModal onClose={(refresh) => { setShowImport(false); if (refresh) fetchCoupons(); }} />}
    </>
  );
}

/* ============================= Merchants ============================= */

function MerchantsTab() {
  const { merchants, loading, verifyMerchant, rejectMerchant, deleteMerchant, fetchMerchants } = useAdminMerchants();
  const { pending, refresh: refreshPending } = usePendingMerchants();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const approve = async (id) => {
    await verifyMerchant(id);
    refreshPending();
  };
  const confirmReject = async () => {
    if (!rejectingId) return;
    await rejectMerchant(rejectingId, rejectReason);
    setRejectingId(null); setRejectReason('');
    refreshPending(); fetchMerchants();
  };

  if (loading) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="Merchants"
        subtitle={`${merchants.length} merchants total · ${pending.length} pending approval`}
        action={<button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Add Merchant</button>}
      />

      {pending.length > 0 && (
        <div className="ds-panel" style={{ marginBottom: 16, border: '1px solid #FCD34D', background: 'linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
          <div className="ds-panel-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ width: 28, height: 28, borderRadius: 8, background: '#F59E0B', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>!</span>
              <p style={{ fontWeight: 700, fontSize: 15 }}>Pending merchant approvals ({pending.length})</p>
            </div>
            <p style={{ fontSize: 12.5, color: '#92400E', marginBottom: 14 }}>
              These merchants self-registered and are awaiting admin review. Approve to publish them publicly, or reject to detach the owner and hide the listing.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pending.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff', border: '1px solid #FDE68A', borderRadius: 10 }}>
                  <MerchantLogo merchant={m} size={36} radius={8} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</p>
                    <p style={{ fontSize: 12, color: '#8B94A8' }}>{m.website?.replace(/^https?:\/\//, '') || 'no website'} · created {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</p>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => approve(m.id)}><i className="bi bi-check-lg" /> Approve</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setRejectingId(m.id); setRejectReason(''); }}>Reject…</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="ds-panel">
        <div className="ds-panel-body-flush" style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead>
              <tr><th>Merchant</th><th>Category</th><th>Network</th><th>Commission</th><th>Status</th><th style={{ width: 160 }}>Actions</th></tr>
            </thead>
            <tbody>
              {merchants.length === 0 ? (
                <tr><td colSpan={6}><EmptyState icon={<i className="bi bi-shop" />} title="No merchants yet" subtitle="Add your first merchant partner." /></td></tr>
              ) : merchants.map(m => (
                <tr key={m.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <MerchantLogo merchant={m} size={34} radius={8} />
                      <div>
                        <p style={{ fontWeight: 600 }}>{m.name}</p>
                        <p style={{ fontSize: 11, color: '#8B94A8' }}>{m.website?.replace(/^https?:\/\//, '')}</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>{m.category?.name || <span style={{ color: '#8B94A8' }}>—</span>}</td>
                  <td style={{ fontSize: 13, textTransform: 'capitalize' }}>{m.affiliate_network && m.affiliate_network !== 'none' ? m.affiliate_network : <span style={{ color: '#8B94A8' }}>Direct</span>}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{m.commission_pct > 0 ? `${m.commission_pct}%` : <span style={{ color: '#8B94A8', fontWeight: 400 }}>—</span>}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexDirection: 'column' }}>
                      <span className={`badge ${m.is_active ? 'badge-green' : 'badge-red'}`}>{m.is_active ? '● Active' : '● Inactive'}</span>
                      {!m.is_verified && <span className="badge badge-yellow">Unverified</span>}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!m.is_verified && <button className="ds-inline-btn success" onClick={() => approve(m.id)}>Approve</button>}
                      {!m.is_verified && <button className="ds-inline-btn" onClick={() => { setRejectingId(m.id); setRejectReason(''); }}>Reject</button>}
                      <button className="ds-inline-btn" onClick={() => setEditing(m)}>Edit</button>
                      <button className="ds-inline-btn danger" onClick={() => setDeleteId(m.id)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!deleteId}
        title="Remove Merchant"
        message="This will remove the merchant from PerkVora. Their deals will be deactivated."
        danger confirmLabel="Remove"
        onConfirm={async () => { await deleteMerchant(deleteId); setDeleteId(null); refreshPending(); }}
        onCancel={() => setDeleteId(null)}
      />
      {rejectingId && (
        <div className="modal-overlay" onClick={() => setRejectingId(null)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Reject merchant</span>
              <button className="modal-close" onClick={() => setRejectingId(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: '#5A6378', marginBottom: 14 }}>
                Rejection keeps the row for audit history but hides it publicly and unbinds any merchant_admin users. Add a short reason (optional) — it will be saved in the audit log.
              </p>
              <textarea className="form-input" rows={3} placeholder="Reason (optional)…" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                <button className="btn btn-secondary" onClick={() => setRejectingId(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={confirmReject}>Reject merchant</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showForm && <MerchantFormModal onClose={() => { setShowForm(false); fetchMerchants(); refreshPending(); }} />}
      {editing  && <MerchantFormModal merchant={editing} onClose={() => { setEditing(null); fetchMerchants(); refreshPending(); }} />}
    </>
  );
}

/* ============================= App Clients ============================= */

function AppClientsTab() {
  const { clients, loading, refresh, updateClient, fetchCredentials, rotateSecret, regenerateKey } = useAppClients();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingCreds, setViewingCreds] = useState(null);

  if (loading) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="App Clients"
        subtitle="Applications consuming the PerkVora API"
        action={<button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Register App</button>}
      />

      {clients.length === 0 ? (
        <div className="ds-panel">
          <div className="ds-panel-body">
            <EmptyState icon={<i className="bi bi-plug" />} title="No app clients yet" subtitle="Register your first app to start issuing API keys." />
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
          {clients.map(c => (
            <div key={c.id} className="ds-card">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: c.brand_color || '#6B2FD9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 18, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', flexShrink: 0 }}>
                  {c.app_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 700, fontSize: 15, color: '#0F1225' }}>{c.app_name}</p>
                  <p style={{ fontSize: 12, color: '#8B94A8', fontFamily: 'ui-monospace, monospace' }}>{c.app_id}</p>
                </div>
                <span className={`badge ${c.is_active ? 'badge-green' : 'badge-red'}`}>{c.is_active ? '● Active' : '● Inactive'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, padding: '12px 14px', background: '#FAFBFD', borderRadius: 10 }}>
                <div><p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Plan</p><p style={{ fontWeight: 700, marginTop: 2, textTransform: 'capitalize' }}>{c.plan}</p></div>
                <div><p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Users</p><p style={{ fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{c.users_count?.toLocaleString() ?? '—'}</p></div>
                <div><p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Redemptions</p><p style={{ fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{c.redemptions_count?.toLocaleString() ?? '—'}</p></div>
                <div><p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Country</p><p style={{ fontWeight: 700, marginTop: 2 }}>{c.country || '—'}</p></div>
              </div>

              <div style={{ marginTop: 14 }}>
                <p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Allowed Origins ({c.allowed_origins?.length || 0})</p>
                {c.allowed_origins?.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {c.allowed_origins.map(o => (
                      <span key={o} title={o} style={{ display: 'inline-block', maxWidth: '100%', padding: '4px 9px', background: '#EBE0FF', color: '#6B2FD9', borderRadius: 6, fontSize: 11, fontFamily: 'ui-monospace, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o}</span>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: '#8B94A8', fontStyle: 'italic' }}>No origins — this tenant can only be reached via X-API-Key.</p>
                )}
              </div>

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setViewingCreds(c)}><i className="bi bi-key" /> Credentials</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(c)}><i className="bi bi-gear" /> Manage</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <AppClientFormModal onClose={() => setShowForm(false)} onCreated={refresh} />}
      {viewingCreds && (
        <AppClientCredentialsModal
          client={viewingCreds}
          onClose={() => setViewingCreds(null)}
          fetchCredentials={fetchCredentials}
          rotateSecret={rotateSecret}
          regenerateKey={regenerateKey}
        />
      )}
      {editing && (
        <AppClientEditModal
          client={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => { await updateClient(editing.id, payload); setEditing(null); }}
        />
      )}
    </>
  );
}

/**
 * One credential row: masked by default, with a reveal toggle and a copy
 * button. The value is only ever in memory — nothing is written to storage.
 */
function SecretField({ label, value, hint, monoColor = '#F97316', defaultShown = false }) {
  const [shown, setShown] = useState(defaultShown);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the value is on screen to copy by hand */ }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {label}
        {hint && <span style={{ fontSize: 11, color: '#8B94A8', fontWeight: 500 }}>{hint}</span>}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <div style={{
          flex: 1, minWidth: 0, background: '#0E0F2E', borderRadius: 10, padding: '11px 14px',
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13,
          color: monoColor, wordBreak: 'break-all', lineHeight: 1.5,
        }}>
          {shown ? value : '•'.repeat(28)}
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShown(s => !s)}
                title={shown ? 'Hide' : 'Show'} style={{ whiteSpace: 'nowrap' }}>
          <i className={shown ? 'bi bi-eye-slash' : 'bi bi-eye'} /> {shown ? 'Hide' : 'Show'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={copy}
                title="Copy" style={{ whiteSpace: 'nowrap' }}>
          <i className={copied ? 'bi bi-check2' : 'bi bi-clipboard'} /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/**
 * App Clients → Credentials. Shows the tenant's X-API-Key and X-API-Secret
 * behind a reveal toggle, and can issue fresh ones.
 *
 * The secret only comes back when a readable copy was stored with it. Secrets
 * issued before that existed are hash-only, so the server says so rather than
 * showing a digest, and rotating is the way to get a viewable one.
 */
function AppClientCredentialsModal({ client, onClose, fetchCredentials, rotateSecret, regenerateKey }) {
  const [creds, setCreds]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState('');
  const [confirming, setConfirming] = useState('');
  const [justIssued, setJustIssued] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchCredentials(client.id);
        if (alive) setCreds(data);
      } catch (err) {
        if (alive) setError(err.response?.data?.message || 'Could not load credentials.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.id, fetchCredentials]);

  const doRotateSecret = async () => {
    setBusy('secret'); setError(''); setConfirming('');
    try {
      const data = await rotateSecret(client.id);
      setCreds(c => ({ ...c, api_secret: data.api_secret, secret_retrievable: true, reason: null }));
      setJustIssued('secret');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not rotate the secret.');
    } finally { setBusy(''); }
  };

  const doRegenerateKey = async () => {
    setBusy('key'); setError(''); setConfirming('');
    try {
      const data = await regenerateKey(client.id);
      setCreds(c => ({ ...c, api_key: data.api_key }));
      setJustIssued('key');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not regenerate the key.');
    } finally { setBusy(''); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Partner API Credentials — {client.app_name}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <p style={{ color: '#8B94A8', fontSize: 14 }}>Loading credentials…</p>
          ) : (
            <>
              {error && <div className="alert alert-danger">{error}</div>}
              {justIssued && (
                <div className="alert alert-success">
                  ✓ New X-API-{justIssued === 'secret' ? 'Secret' : 'Key'} issued
                  {justIssued === 'secret' && ' and shown below — copy it now'}. The previous value stopped working
                  immediately, so update <strong>{client.app_id}</strong> in the consuming app straight away.
                </div>
              )}

              <div style={{ fontSize: 13, color: '#8B94A8', marginBottom: 14, fontFamily: 'ui-monospace, monospace' }}>
                app_id: <span style={{ color: '#0F1225', fontWeight: 700 }}>{client.app_id}</span>
              </div>

              <SecretField label="X-API-Key" value={creds?.api_key || ''} />

              {creds?.secret_retrievable ? (
                // Remounted whenever the value changes so a freshly issued
                // secret is revealed straight away — you need to copy it now,
                // and hiding it behind a second click is just friction.
                <SecretField
                  key={creds.api_secret}
                  label="X-API-Secret"
                  value={creds.api_secret}
                  monoColor="#D946EF"
                  defaultShown={justIssued === 'secret'}
                />
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <label className="form-label">X-API-Secret</label>
                  <div className="alert alert-info" style={{ marginBottom: 0 }}>
                    <p style={{ marginBottom: 12 }}>{creds?.reason || 'This secret cannot be displayed.'}</p>
                    {/* The fix belongs next to the problem — not at the far
                        end of a button row where it reads as a sibling of
                        "Regenerate key", which does something else entirely. */}
                    <button className="btn btn-primary btn-sm" disabled={!!busy}
                            onClick={() => setConfirming('secret')}>
                      {busy === 'secret' ? 'Issuing…' : 'Issue a new secret'}
                    </button>
                  </div>
                </div>
              )}

              <p style={{ fontSize: 12, color: '#8B94A8', lineHeight: 1.6, marginBottom: 16 }}>
                Paste these into the consuming app&rsquo;s Admin Panel → Perkvora Settings (API Key and API Secret).
                Rotating either value takes effect at once, so change both ends together.
              </p>

              {confirming ? (
                <div className="alert alert-danger">
                  <p style={{ marginBottom: 10 }}>
                    Issue a new <strong>X-API-{confirming === 'secret' ? 'Secret' : 'Key'}</strong> for{' '}
                    <strong>{client.app_id}</strong>? The current one stops working immediately, so every app using
                    this tenant fails until it is updated.
                    {confirming === 'key' && ' The API secret is not affected.'}
                    {confirming === 'secret' && ' The API key is not affected.'}
                  </p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setConfirming('')}>Cancel</button>
                    <button className="btn btn-primary btn-sm"
                            onClick={confirming === 'secret' ? doRotateSecret : doRegenerateKey}>
                      Yes, issue a new {confirming === 'secret' ? 'secret' : 'key'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {/* Named for the value each one replaces. "Regenerate key"
                      next to "Rotate secret" reads as two ways to do the same
                      thing, and picking the wrong one changes nothing visible. */}
                  <button className="btn btn-secondary" disabled={!!busy} onClick={() => setConfirming('key')}>
                    {busy === 'key' ? 'Issuing…' : 'New API key'}
                  </button>
                  <button className="btn btn-secondary" disabled={!!busy} onClick={() => setConfirming('secret')}>
                    {busy === 'secret' ? 'Issuing…' : 'New API secret'}
                  </button>
                  <button className="btn btn-primary" onClick={onClose}>Done</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Textarea + chip preview + per-line validation for app_clients.allowed_origins.
 * Each line must be a complete web origin: https://host[:port] (no path, no
 * trailing slash). Browsers send exactly this in the Origin header.
 */
function OriginsField({ value, onChange, error }) {
  const [text, setText] = useState((value || []).join('\n'));
  const ORIGIN_RE = /^https?:\/\/[^/?#\s]+$/;

  // Each render: parse text → array → bubble up. Local state preserves
  // mid-typing whitespace/blank lines so the textarea isn't re-flowed
  // out from under the cursor.
  const lines = text.split('\n').map(l => l.trim());
  const cleaned = Array.from(new Set(lines.filter(Boolean)));
  const invalid = cleaned.filter(l => !ORIGIN_RE.test(l));

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    const parsed = Array.from(new Set(next.split('\n').map(l => l.trim()).filter(Boolean)));
    onChange(parsed);
  };

  return (
    <div className="form-group">
      <label className="form-label">
        Allowed Origins
        <span style={{ marginLeft: 8, fontSize: 11, color: '#8B94A8', fontWeight: 500 }}>one per line — used for browser tenant resolution</span>
      </label>
      <textarea
        className="form-input"
        rows={4}
        spellCheck={false}
        placeholder="https://www.example.com&#10;https://dev.example.com&#10;http://localhost:5173"
        value={text}
        onChange={handleChange}
        style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, lineHeight: 1.6, resize: 'vertical' }}
      />
      {invalid.length > 0 && (
        <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>
          ⚠ Invalid: {invalid.slice(0, 3).join(', ')}{invalid.length > 3 ? `, +${invalid.length - 3} more` : ''}.
          Use full origin like <code>https://example.com</code> (no path, no trailing slash).
        </p>
      )}
      {error && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{error}</p>}
      {cleaned.length > 0 && invalid.length === 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {cleaned.map(o => (
            <span key={o} style={{ padding: '3px 8px', background: '#EBE0FF', color: '#6B2FD9', borderRadius: 6, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{o}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function AppClientEditModal({ client, onClose, onSave }) {
  const [form, setForm] = useState({
    app_name:        client.app_name || '',
    website_url:     client.website_url || '',
    contact_email:   client.contact_email || '',
    brand_color:     client.brand_color || '#6B2FD9',
    plan:            client.plan || 'free',
    is_active:       !!client.is_active,
    allowed_origins: client.allowed_origins || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const f = (k) => ({ value: form[k], onChange: (e) => setForm(p => ({ ...p, [k]: e.target.value })) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await onSave(form);
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      if (fieldErrors) {
        const first = Object.values(fieldErrors).flat()[0];
        setError(first || 'Save failed');
      } else {
        setError(err.response?.data?.message || 'Save failed');
      }
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Manage {client.app_name}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ background: '#FAFBFD', padding: '10px 12px', borderRadius: 8, fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#5A6378', marginBottom: 16 }}>
              <strong style={{ color: '#0F1225' }}>{client.app_id}</strong> · created {client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'}
            </div>

            <div className="form-group"><label className="form-label">App Name</label><input className="form-input" required {...f('app_name')} /></div>
            <div className="form-group"><label className="form-label">Website</label><input className="form-input" type="url" placeholder="https://example.com" {...f('website_url')} /></div>
            <div className="form-group"><label className="form-label">Contact Email</label><input className="form-input" type="email" {...f('contact_email')} /></div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group"><label className="form-label">Brand Color</label><input className="form-input" type="color" {...f('brand_color')} style={{ padding: 4, height: 44 }} /></div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="form-input form-select" {...f('plan')}>
                  <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Active</span>
                <span style={{ fontSize: 12, color: '#8B94A8' }}>(disabling blocks all API calls for this tenant)</span>
              </label>
            </div>

            <OriginsField value={form.allowed_origins} onChange={(v) => setForm(p => ({ ...p, allowed_origins: v }))} />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================= Cashback Pools ============================= */

function CashbackPoolsTab() {
  const { pools, loading, createPool, updatePool, deletePool } = useAdminCashbackPools();
  const { clients } = useAppClients();
  const [showForm, setShowForm] = useState(false);
  const [editPool, setEditPool] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  if (loading) return <LoadingPage />;

  const totalBudget = pools.reduce((sum, p) => sum + Number(p.total_budget || 0), 0);
  const totalUsed   = pools.reduce((sum, p) => sum + Number(p.used_amount  || 0), 0);

  return (
    <>
      <PageHeader
        title="Cashback Pools"
        subtitle={`${pools.length} pool${pools.length !== 1 ? 's' : ''} · $${totalUsed.toFixed(2)} of $${totalBudget.toFixed(2)} budget used`}
        action={<button className="btn btn-primary" onClick={() => { setEditPool(null); setShowForm(true); }}>+ New Pool</button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard gradient label="Total Budget"    value={`$${totalBudget.toFixed(2)}`} icon={<i className="bi bi-cash-stack" />} />
        <StatCard         label="Cashback Awarded" value={`$${totalUsed.toFixed(2)}`} icon={<i className="bi bi-cash-coin" />} iconBg="linear-gradient(135deg,#0F9D6E,#059669)" />
        <StatCard         label="Remaining"        value={`$${(totalBudget - totalUsed).toFixed(2)}`} icon={<i className="bi bi-bank" />} iconBg="linear-gradient(135deg,#F59E0B,#D97706)" />
        <StatCard         label="Active Pools"     value={pools.filter(p => p.is_active).length} icon={<i className="bi bi-check2-circle" />} iconBg="linear-gradient(135deg,#8B5CF6,#6B2FD9)" />
      </div>

      {pools.length === 0 ? (
        <div className="ds-panel">
          <div className="ds-panel-body">
            <EmptyState
              icon={<i className="bi bi-cash-stack" />}
              title="No cashback pools yet"
              subtitle="Create a pool to start awarding cashback on redemptions for an app."
              action={<button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Create First Pool</button>}
            />
          </div>
        </div>
      ) : (
        <div className="ds-panel">
          <div className="ds-panel-body-flush" style={{ overflowX: 'auto' }}>
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>App</th>
                  <th>Rate</th>
                  <th style={{ textAlign: 'right' }}>Used / Budget</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pools.map(p => {
                  const usedPct = p.total_budget ? Math.min(100, (p.used_amount / p.total_budget) * 100) : 0;
                  return (
                    <tr key={p.id}>
                      <td>
                        <p style={{ fontWeight: 600, color: '#0F1225' }}>{p.campaign_name}</p>
                        <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 2 }}>
                          {p.total_claims ?? 0} claim{p.total_claims !== 1 ? 's' : ''} · {p.paid_claims ?? 0} paid
                        </p>
                      </td>
                      <td style={{ fontSize: 13 }}>{p.app_client?.app_name || <span style={{ color: '#8B94A8' }}>—</span>}</td>
                      <td>
                        <span className="badge badge-gradient">
                          {p.cashback_type === 'percent' ? `${p.cashback_value}%` : `$${p.cashback_value}`}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        <p style={{ fontWeight: 700 }}>${Number(p.used_amount).toFixed(2)} / ${Number(p.total_budget).toFixed(2)}</p>
                        <div style={{ height: 4, background: '#F0F2F8', borderRadius: 999, overflow: 'hidden', marginTop: 4 }}>
                          <div style={{ height: '100%', width: `${usedPct}%`, background: usedPct > 80 ? 'var(--danger)' : 'var(--brand-gradient)', transition: 'width 0.3s' }} />
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#8B94A8' }}>
                        {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : '—'}
                      </td>
                      <td><span className={`badge ${p.is_active ? 'badge-green' : 'badge-red'}`}>{p.is_active ? '● Active' : '● Paused'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="ds-inline-btn" onClick={() => { setEditPool(p); setShowForm(true); }}>Edit</button>
                          <button className="ds-inline-btn danger" onClick={() => setDeleteId(p.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleteId}
        title="Delete Cashback Pool"
        message="If this pool has claims, it will be deactivated instead of deleted (to preserve audit history)."
        danger confirmLabel="Delete"
        onConfirm={async () => { await deletePool(deleteId); setDeleteId(null); }}
        onCancel={() => setDeleteId(null)}
      />
      {showForm && (
        <CashbackPoolFormModal
          pool={editPool}
          clients={clients}
          onClose={() => { setShowForm(false); setEditPool(null); }}
          onSave={async (payload) => {
            if (editPool) await updatePool(editPool.id, payload);
            else          await createPool(payload);
            setShowForm(false); setEditPool(null);
          }}
        />
      )}
    </>
  );
}

function CashbackPoolFormModal({ pool, clients, onClose, onSave }) {
  const isEdit = !!pool;
  const [form, setForm] = useState({
    app_client_id:  pool?.app_client_id  || (clients[0]?.id ?? ''),
    campaign_name:  pool?.campaign_name  || '',
    total_budget:   pool?.total_budget   || '',
    cashback_type:  pool?.cashback_type  || 'percent',
    cashback_value: pool?.cashback_value || '',
    expires_at:     pool?.expires_at?.slice(0, 10) || '',
    is_active:      pool ? pool.is_active : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const f = (k) => ({ value: form[k], onChange: (e) => setForm((p) => ({ ...p, [k]: e.target.value })) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.expires_at) delete payload.expires_at;
      if (isEdit) delete payload.app_client_id;
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Could not save pool.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Cashback Pool' : 'New Cashback Pool'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            {!isEdit && (
              <div className="form-group">
                <label className="form-label">App Client *</label>
                <select className="form-input form-select" required {...f('app_client_id')}>
                  {clients.map((c) => (<option key={c.id} value={c.id}>{c.app_name} ({c.app_id})</option>))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Campaign Name *</label>
              <input className="form-input" required maxLength={140} {...f('campaign_name')}
                placeholder="e.g. Q2 2026 Cashback Promo" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Total Budget (USD) *</label>
                <input className="form-input" type="number" min="0" step="0.01" required {...f('total_budget')} placeholder="10000" />
              </div>
              <div className="form-group">
                <label className="form-label">Cashback Type *</label>
                <select className="form-input form-select" {...f('cashback_type')}>
                  <option value="percent">Percent of order</option>
                  <option value="fixed">Fixed $ per redemption</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{form.cashback_type === 'percent' ? 'Cashback %' : 'Cashback $'} *</label>
                <input className="form-input" type="number" min="0" step="0.01" required {...f('cashback_value')}
                  placeholder={form.cashback_type === 'percent' ? '5' : '2.50'} />
              </div>
              <div className="form-group">
                <label className="form-label">Expires At</label>
                <input className="form-input" type="date" {...f('expires_at')} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4A5568', cursor: 'pointer', marginBottom: 18 }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#6B2FD9' }} />
              Active (eligible for cashback awards)
            </label>
            {isEdit && (
              <p style={{ fontSize: 12, color: '#8B94A8', marginBottom: 18, padding: '10px 14px', background: '#F7F8FC', borderRadius: 8 }}>
                Used so far: <strong>${Number(pool.used_amount).toFixed(2)}</strong> · Cannot lower budget below this amount.
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Pool')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================= Users (placeholder) ============================= */

function UsersTab() {
  const { users, meta, loading, query, setQuery, page, setPage, includePending, setIncludePending, refresh } = useAdminUsers();
  const { user: me } = useAuth();
  const { toast } = useToast();
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [busy, setBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const toggle = (id) => setSelected(s => {
    const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const toggleAll = () => {
    const all = users.map(u => u.id);
    setSelected(s => s.size === all.length ? new Set() : new Set(all));
  };

  const runBulk = () => {
    // Was: window.confirm(...). Replaced with an in-app ConfirmModal so
    // the bulk action confirmation matches the rest of the UI instead of
    // popping a system dialog labeled "dev.perkvora.com says".
    if (!bulkAction || selected.size === 0) return;
    setPendingConfirm(true);
  };

  const executeBulk = async () => {
    setPendingConfirm(false);
    setBusy(true); setBulkResult(null);
    try {
      const result = await bulkUserAction(bulkAction, Array.from(selected));
      setBulkResult(result.results);
      setSelected(new Set());
      refresh();
    } catch (err) {
      toast.error('Bulk action failed', err.response?.data?.message);
    } finally { setBusy(false); }
  };

  // Triggers the in-app ConfirmModal (below). Replaces the previous
  // window.confirm() so the prompt no longer renders as a system dialog
  // labeled "dev.perkvora.com says".
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const requestImpersonate = (target) => {
    if (target.role === 'super_admin') { toast.error('Cannot impersonate another super admin.'); return; }
    if (target.id === me?.id)          { toast.error('Cannot impersonate yourself.'); return; }
    setImpersonateTarget(target);
  };
  const performImpersonate = async () => {
    const target = impersonateTarget;
    if (!target) return;
    setImpersonateTarget(null);
    try {
      const res = await startImpersonation(target.id);
      // Stash current admin session so we can restore on stop.
      const adminToken = localStorage.getItem('pv_token');
      const adminUser  = localStorage.getItem('pv_user');
      localStorage.setItem('pv_admin_stash', JSON.stringify({ token: adminToken, user: adminUser, expires_at: res.expires_at }));
      localStorage.setItem('pv_token', res.token);
      localStorage.setItem('pv_user', JSON.stringify(res.user));
      window.location.assign('/');  // hard reload as the impersonated user
    } catch (err) {
      toast.error('Impersonation failed', err.response?.data?.error);
    }
  };

  return (
    <>
      <PageHeader
        title="Users"
        subtitle={meta.total != null ? `${meta.total.toLocaleString()} users across all tenants` : 'Registered shoppers, merchants, and admins'}
      />

      <div className="ds-panel" style={{ marginBottom: 16 }}>
        <div className="ds-panel-body" style={{ display: 'grid', gap: 10 }}>
          <input className="form-input" placeholder="Search by name, email, or phone…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5A6378', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => { setIncludePending(e.target.checked); setPage(1); }}
            />
            Include unverified signups
            <span style={{ fontSize: 12, color: '#8B94A8' }}>
              · Off by default — keeps the list to users who finished email verification.
            </span>
          </label>
          {selected.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} selected</span>
              <select className="form-input form-select" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} style={{ flex: 1, maxWidth: 280 }}>
                <option value="">Choose bulk action…</option>
                <option value="activate">Mark email verified</option>
                <option value="deactivate">Revoke verification + tokens</option>
                <option value="force_logout">Force logout (revoke tokens)</option>
                <option value="force_password_reset">Force password reset</option>
                <option value="delete">Soft delete</option>
              </select>
              <button className="btn btn-primary btn-sm" disabled={!bulkAction || busy} onClick={runBulk}>{busy ? 'Working…' : 'Apply'}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
            </div>
          )}
          {bulkResult && (
            <div style={{ fontSize: 12, padding: '8px 12px', background: '#DCFCE7', borderRadius: 6 }}>
              ✓ {bulkResult.ok.length} ok · {bulkResult.skipped.length} skipped · {bulkResult.failed.length} failed
            </div>
          )}
        </div>
      </div>

      {loading ? <LoadingPage /> : users.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-people" />} title="No users match" /></div></div>
      ) : (
        <div className="ds-panel" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ background: '#FAFBFD' }}>
                  <th style={{ padding: '12px 14px', textAlign: 'left', width: 32 }}>
                    <input type="checkbox" checked={selected.size === users.length && users.length > 0} onChange={toggleAll} />
                  </th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>User</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Role</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tenant</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Spatie</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Joined</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right', color: '#5A6378', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isLocked = u.id === me?.id || u.role === 'super_admin';
                  return (
                    <tr key={u.id} style={{ borderTop: '1px solid #F0F2F8', background: selected.has(u.id) ? '#FAF7FF' : 'transparent' }}>
                      <td style={{ padding: '12px 14px' }}>
                        <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} disabled={isLocked} title={isLocked ? 'Self / super_admin protected' : ''} />
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13 }}>
                        <div style={{ fontWeight: 600 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: '#8B94A8' }}>{u.email}</div>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13 }}>
                        {u.email_verified_at ? (
                          <span title={`Verified ${new Date(u.email_verified_at).toLocaleString()}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontSize: 11.5, fontWeight: 700 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E' }} />
                            Verified
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', fontSize: 11.5, fontWeight: 700 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#F59E0B' }} />
                            Pending
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13 }}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: u.role === 'super_admin' ? '#FEE2E2' : u.role === 'merchant_admin' ? '#DBEAFE' : '#F0F2F8', color: u.role === 'super_admin' ? '#991B1B' : u.role === 'merchant_admin' ? '#1E40AF' : '#5A6378', fontSize: 11.5, fontWeight: 600 }}>
                          {u.role}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13 }}>{u.app_client?.app_id || '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 13 }}>
                        {u.spatie_roles?.length > 0
                          ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {u.spatie_roles.map(r => <span key={r} style={{ padding: '2px 6px', background: '#EBE0FF', color: '#6B2FD9', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{r}</span>)}
                            </div>
                          : <span style={{ color: '#8B94A8' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#5A6378' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <button className="btn btn-secondary btn-sm" disabled={isLocked} onClick={() => requestImpersonate(u)} title={isLocked ? 'Self / super_admin protected' : 'Impersonate this user'}><i className="bi bi-person" /> Impersonate</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {meta.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(Math.max(1, page - 1))}>← Prev</button>
          <span style={{ padding: '8px 14px', fontSize: 13, color: '#5A6378' }}>Page {meta.current_page} of {meta.last_page}</span>
          <button className="btn btn-secondary btn-sm" disabled={page === meta.last_page} onClick={() => setPage(Math.min(meta.last_page, page + 1))}>Next →</button>
        </div>
      )}

      <ConfirmModal
        open={pendingConfirm}
        title="Apply bulk action?"
        message={`This will apply "${bulkAction}" to ${selected.size} selected user(s). Continue?`}
        confirmLabel="Apply"
        onConfirm={executeBulk}
        onCancel={() => setPendingConfirm(false)}
      />
      <ConfirmModal
        open={!!impersonateTarget}
        title="Impersonate user?"
        message={impersonateTarget
          ? `You're about to sign in as ${impersonateTarget.name || impersonateTarget.email} (${impersonateTarget.email}). Your admin session will be safely stashed and you can return with the "Stop impersonating" banner.`
          : ''}
        confirmLabel="Impersonate"
        onConfirm={performImpersonate}
        onCancel={() => setImpersonateTarget(null)}
      />
    </>
  );
}

/* ============================= Fraud Log (placeholder) ============================= */

function FraudTab() {
  return (
    <>
      <PageHeader title="Fraud Log" subtitle="Flagged redemptions, IP velocity events, and device anomalies" />
      <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-shield-exclamation" />} title="No fraud events" subtitle="All quiet. Fraud events will appear here in real-time as the detection system flags them." /></div></div>
    </>
  );
}

/* ============================= Audit Log ============================= */

const AUDIT_ACTION_COLORS = {
  'admin.coupon':            { bg: '#EBE0FF', fg: '#6B2FD9' },
  'admin.merchant':          { bg: '#DBEAFE', fg: '#1E40AF' },
  'admin.app_client':        { bg: '#FEF3C7', fg: '#92400E' },
  'admin.cashback_pool':     { bg: '#DCFCE7', fg: '#166534' },
  'admin.coupon_submission': { bg: '#FFE4E6', fg: '#BE123C' },
  'admin.affiliate_feed':    { bg: '#E0E7FF', fg: '#3730A3' },
};

function actionAccent(action = '') {
  for (const prefix in AUDIT_ACTION_COLORS) {
    if (action.startsWith(prefix)) return AUDIT_ACTION_COLORS[prefix];
  }
  return { bg: '#F0F2F8', fg: '#5A6378' };
}

function shortType(type) {
  if (!type) return '—';
  return type.split('\\').pop();
}

function AuditLogTab() {
  const [draft, setDraft] = useState({ q: '', action: '', target_type: '', from: '', to: '' });
  const { logs, meta, loading, refresh } = useAuditLogs({ page: 1, per_page: 30 });
  const { stats } = useAuditStats(30);
  const { toast } = useToast();
  const [page, setPage] = useState(1);

  const apply = () => {
    setPage(1);
    refresh({ ...draft, page: 1, per_page: 30 });
  };
  const reset = () => {
    const blank = { q: '', action: '', target_type: '', from: '', to: '' };
    setDraft(blank); setPage(1);
    refresh({ ...blank, page: 1, per_page: 30 });
  };
  const goPage = (p) => {
    setPage(p);
    refresh({ ...draft, page: p, per_page: 30 });
  };

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries({ ...draft, limit: 10000 }).filter(([, v]) => v !== '' && v != null));
      const res = await api.get('/admin/audit-logs/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `perkvora-audit-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 429) toast.error('Export rate-limited', 'Try again in a minute.');
      else                              toast.error('Export failed', err.response?.data?.message);
    }
  };

  const byAction  = stats.by_action || [];
  const topActors = stats.top_actors || [];

  return (
    <>
      <PageHeader
        title="Audit Log"
        subtitle={meta.total != null ? `${meta.total.toLocaleString()} entries · ${stats.total?.toLocaleString() ?? 0} in last ${stats.window_days ?? 30}d` : 'Recent admin actions'}
        action={<button className="btn btn-secondary" onClick={exportCsv}>Export CSV</button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard label="Total (30d)" value={(stats.total ?? 0).toLocaleString()} icon={<i className="bi bi-clipboard-data" />} />
        <StatCard label="Distinct actions" value={stats.distinct_actions ?? 0} icon={<i className="bi bi-bullseye" />} />
        <StatCard label="Top action" value={byAction[0]?.action ?? '—'} icon={<i className="bi bi-fire" />} />
        <StatCard label="Top actor" value={topActors[0]?.user?.email ?? '—'} icon={<i className="bi bi-person" />} />
      </div>

      {/* Filters */}
      <div className="ds-panel" style={{ marginBottom: 18 }}>
        <div className="ds-panel-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div className="form-group"><label className="form-label">Search action</label><input className="form-input" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} placeholder="admin.coupon" /></div>
            <div className="form-group"><label className="form-label">Exact action</label><input className="form-input" value={draft.action} onChange={(e) => setDraft({ ...draft, action: e.target.value })} placeholder="admin.merchant.verify" /></div>
            <div className="form-group"><label className="form-label">Target type</label><input className="form-input" value={draft.target_type} onChange={(e) => setDraft({ ...draft, target_type: e.target.value })} placeholder="App\Models\Coupon" /></div>
            <div className="form-group"><label className="form-label">From</label><input className="form-input" type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">To</label><input className="form-input" type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button className="btn btn-primary btn-sm" onClick={apply}>Apply filters</button>
            <button className="btn btn-secondary btn-sm" onClick={reset}>Reset</button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingPage />
      ) : logs.length === 0 ? (
        <div className="ds-panel">
          <div className="ds-panel-body">
            <EmptyState icon={<i className="bi bi-clipboard-data" />} title="No audit entries match" subtitle="Try widening the date range or clearing filters. Admin actions will appear here as they happen." />
          </div>
        </div>
      ) : (
        <div className="ds-panel" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ background: '#FAFBFD' }}>
                  <th style={auditTh}>When</th>
                  <th style={auditTh}>Action</th>
                  <th style={auditTh}>Actor</th>
                  <th style={auditTh}>Target</th>
                  <th style={auditTh}>IP</th>
                  <th style={auditTh}>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(row => {
                  const accent = actionAccent(row.action);
                  return (
                    <tr key={row.id} style={{ borderTop: '1px solid #F0F2F8' }}>
                      <td style={auditTd} title={row.created_at}>
                        {new Date(row.created_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={auditTd}>
                        <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 6, background: accent.bg, color: accent.fg, fontSize: 11.5, fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                          {row.action}
                        </span>
                      </td>
                      <td style={auditTd}>
                        {row.user ? (
                          <>
                            <div style={{ fontWeight: 600 }}>{row.user.email}</div>
                            <div style={{ fontSize: 11, color: '#8B94A8' }}>{row.user.role} · #{row.user.id}</div>
                          </>
                        ) : <span style={{ color: '#8B94A8' }}>system</span>}
                      </td>
                      <td style={auditTd}>
                        {row.target_type ? (
                          <>
                            <div style={{ fontWeight: 600 }}>{shortType(row.target_type)}</div>
                            {row.target_id && <div style={{ fontSize: 11, color: '#8B94A8' }}>#{row.target_id}</div>}
                          </>
                        ) : <span style={{ color: '#8B94A8' }}>—</span>}
                      </td>
                      <td style={{ ...auditTd, fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#5A6378' }}>{row.ip || '—'}</td>
                      <td style={auditTd}>
                        <AuditMetadataPreview value={row.metadata} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {meta.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => goPage(Math.max(1, page - 1))}>← Prev</button>
          <span style={{ padding: '8px 14px', fontSize: 13, color: '#5A6378' }}>Page {meta.current_page} of {meta.last_page}</span>
          <button className="btn btn-secondary btn-sm" disabled={page === meta.last_page} onClick={() => goPage(Math.min(meta.last_page, page + 1))}>Next →</button>
        </div>
      )}
    </>
  );
}

const auditTh = { padding: '12px 14px', textAlign: 'left', color: '#5A6378', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' };
const auditTd = { padding: '12px 14px', color: '#0F1225', fontSize: 13, verticalAlign: 'top' };

const OUTCOME_STYLE = {
  success: { bg: '#DCFCE7', fg: '#166534', label: '✓ success' },
  partial: { bg: '#FEF3C7', fg: '#92400E', label: '◐ partial' },
  failure: { bg: '#FEE2E2', fg: '#991B1B', label: '✗ failed'  },
};

function AuditMetadataPreview({ value }) {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
    return <span style={{ color: '#8B94A8' }}>—</span>;
  }
  // Surface `outcome` as a colored pill so readers don't have to parse the
  // JSON to see whether the action succeeded. The old preview rendered the
  // full payload as monospace JSON — that included counters like
  // `failed: 0` which QA misread as "the action failed".
  const outcome = typeof value === 'object' ? value.outcome : null;
  const accent  = outcome && OUTCOME_STYLE[outcome];
  const text  = typeof value === 'string' ? value : JSON.stringify(value);
  const short = text.length > 80 ? text.slice(0, 78) + '…' : text;
  return (
    <span title={text} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: '#5A6378', wordBreak: 'break-all' }}>
      {accent && (
        <span style={{ padding: '2px 6px', borderRadius: 5, background: accent.bg, color: accent.fg, fontWeight: 700, fontFamily: 'system-ui, sans-serif', fontSize: 11, whiteSpace: 'nowrap' }}>
          {accent.label}
        </span>
      )}
      <span>{short}</span>
    </span>
  );
}

/* ============================= Roles ============================= */

function RolesTab() {
  const { users, meta, loading, query, setQuery, page, setPage, refresh } = useAdminUsers();
  const [editing, setEditing] = useState(null);

  return (
    <>
      <PageHeader
        title="Roles"
        subtitle="Grant ops staff granular access via Spatie roles. The legacy users.role enum still drives core gating; these stack on top."
      />

      <div className="ds-panel" style={{ marginBottom: 16 }}>
        <div className="ds-panel-body">
          <input className="form-input" placeholder="Search users by name, email, or phone…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
        </div>
      </div>

      {loading ? <LoadingPage /> : users.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-shield-lock" />} title="No users match" /></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {users.map(u => (
            <div key={u.id} className="ds-card" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 14, padding: '12px 18px' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #6B2FD9, #D946EF)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                {u.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                <div style={{ fontSize: 12, color: '#8B94A8' }}>{u.email} · {u.role}</div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 240, justifyContent: 'flex-end' }}>
                {(u.spatie_roles?.length > 0)
                  ? u.spatie_roles.map(r => <span key={r} style={{ padding: '2px 6px', background: '#EBE0FF', color: '#6B2FD9', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{r}</span>)
                  : <span style={{ color: '#8B94A8', fontSize: 12 }}>no extra roles</span>}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(u)}>Edit roles</button>
            </div>
          ))}
        </div>
      )}

      {meta.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(Math.max(1, page - 1))}>← Prev</button>
          <span style={{ padding: '8px 14px', fontSize: 13, color: '#5A6378' }}>Page {meta.current_page} of {meta.last_page}</span>
          <button className="btn btn-secondary btn-sm" disabled={page === meta.last_page} onClick={() => setPage(Math.min(meta.last_page, page + 1))}>Next →</button>
        </div>
      )}

      {editing && (
        <RoleEditorModal
          user={editing}
          onClose={() => setEditing(null)}
          onChanged={refresh}
        />
      )}
    </>
  );
}

function RoleEditorModal({ user, onClose, onChanged }) {
  const { toast } = useToast();
  const { roles, loading: catalogueLoading } = useRoleCatalogue();
  const [assigned, setAssigned] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUserRoles(user.id)
      .then(data => setAssigned(new Set(data.roles || [])))
      .catch(() => {});
  }, [user.id]);

  const toggle = async (roleName) => {
    setBusy(roleName); setError('');
    const wasAssigned = assigned.has(roleName);
    try {
      if (wasAssigned) {
        const data = await revokeUserRole(user.id, roleName);
        setAssigned(new Set(data.roles || []));
        toast.success(`Removed "${roleName}" from ${user.name}`);
      } else {
        const data = await assignUserRole(user.id, roleName);
        setAssigned(new Set(data.roles || []));
        toast.success(`Assigned "${roleName}" to ${user.name}`);
      }
      // Refresh the parent user list so spatie_roles badges update
      // immediately. Without this the modal showed the change but the
      // closed modal left stale badges on the user card.
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
      toast.error(err.response?.data?.error || `Could not ${wasAssigned ? 'revoke' : 'assign'} role`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Roles for {user.name}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: '#FAFBFD', padding: '10px 12px', borderRadius: 8, fontSize: 12, color: '#5A6378', marginBottom: 16 }}>
            <strong style={{ color: '#0F1225' }}>{user.email}</strong> · primary role: <code>{user.role}</code>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {catalogueLoading ? <LoadingPage /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {roles.map(r => {
                const on = assigned.has(r.name);
                const isBusy = busy === r.name;
                return (
                  <button key={r.name} disabled={isBusy} onClick={() => toggle(r.name)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '12px 14px', borderRadius: 10, cursor: isBusy ? 'wait' : 'pointer',
                      background: on ? '#EBE0FF' : '#FAFBFD',
                      border: `1px solid ${on ? '#D8C8F8' : '#E4E7F0'}`,
                      fontFamily: 'inherit', textAlign: 'left', width: '100%',
                    }}>
                    <input type="checkbox" checked={on} readOnly style={{ marginTop: 3, accentColor: '#6B2FD9', pointerEvents: 'none' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: on ? '#6B2FD9' : '#0F1225', fontFamily: 'ui-monospace, monospace' }}>{r.name}</div>
                      {r.description && <div style={{ fontSize: 12, color: '#5A6378', marginTop: 3 }}>{r.description}</div>}
                      {r.permissions?.length > 0 && (
                        <div style={{ fontSize: 11, color: '#8B94A8', marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>
                          {r.permissions.slice(0, 6).join(' · ')}{r.permissions.length > 6 ? ` … +${r.permissions.length - 6}` : ''}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <MerchantScopePanel user={user} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Restricts one admin to a set of merchants.
 *
 * An empty selection is "no restriction", not "no access" — an admin who was
 * never assigned anything keeps the platform-wide access they always had.
 * The panel says so out loud rather than leaving an empty list to be read
 * either way. Super admins are never restricted, so it renders read-only.
 */
function MerchantScopePanel({ user }) {
  const { toast } = useToast();
  const { merchants } = useAdminMerchants();
  const [selected, setSelected] = useState(null);   // null until loaded
  const [saving, setSaving]     = useState(false);
  const isSuper = user.role === 'super_admin';

  useEffect(() => {
    if (isSuper) { setSelected([]); return; }
    fetchMerchantScope(user.id)
      .then(d => setSelected((d.merchant_ids || []).map(Number)))
      .catch(() => setSelected([]));
  }, [user.id, isSuper]);

  const toggle = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveMerchantScope(user.id, selected);
      toast.success(res.message || 'Merchant access updated.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update merchant access.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 22, borderTop: '1px solid #E4E7F0', paddingTop: 18 }}>
      <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0F1225', marginBottom: 4 }}>Merchant access</div>
      {isSuper ? (
        <p style={{ fontSize: 12, color: '#5A6378' }}>Super admins can act on every merchant and cannot be restricted.</p>
      ) : selected === null ? (
        <p style={{ fontSize: 12, color: '#8B94A8' }}>Loading…</p>
      ) : (
        <>
          <p style={{ fontSize: 12, color: '#5A6378', marginBottom: 10 }}>
            {selected.length === 0
              ? 'No merchants selected — this admin can access every merchant. Select merchants to restrict them.'
              : `Restricted to ${selected.length} merchant${selected.length === 1 ? '' : 's'}. Clear the selection to remove the restriction.`}
          </p>
          <div style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid #E4E7F0', borderRadius: 10, padding: 8 }}>
            {(merchants || []).length === 0
              ? <p style={{ fontSize: 12, color: '#8B94A8', padding: 6 }}>No merchants to choose from.</p>
              : merchants.map(m => (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} style={{ accentColor: '#6B2FD9' }} />
                  <span>{m.name}</span>
                </label>
              ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-secondary btn-sm" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save merchant access'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Upload an image and keep its URL.
 *
 * Replaces the old bare "Image URL" text box, which required the artwork to
 * already be hosted somewhere (QA 09-09 admin #6). The value handed back is
 * still just a URL, so the coupon record is unchanged — an existing coupon
 * with an externally hosted image keeps working and simply shows a preview.
 */
function ImageUploadField({ value, onChange, purpose = 'coupon' }) {
  const inputRef = useRef(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const ACCEPT = 'image/jpeg,image/png,image/webp';
  const MAX_BYTES = 2 * 1024 * 1024;

  const pick = async (file) => {
    if (!file) return;
    setError('');
    // Checked here as well as on the server so the common mistakes cost
    // nothing — `accept` alone does not stop a determined file picker.
    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
      setError('Images must be JPG, PNG or WebP.'); return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That image is ${(file.size / 1048576).toFixed(1)}MB. The limit is 2MB.`); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', purpose);
      const { data } = await api.post('/admin/uploads/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange((data.data ?? data).url);
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      setError(fieldErrors?.file?.[0] || err.response?.data?.message || 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';   // allow re-picking the same file
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
             onChange={e => pick(e.target.files?.[0])} />

      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={value} alt="Coupon artwork"
               style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10, border: '1px solid #E4E7F0', background: '#FAFBFD' }}
               onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                      onClick={() => inputRef.current?.click()}>
                {busy ? 'Uploading…' : 'Change'}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                      onClick={() => { setError(''); onChange(''); }} style={{ color: '#DC2626' }}>
                Remove
              </button>
            </div>
            <span style={{ fontSize: 11, color: '#8B94A8', wordBreak: 'break-all', maxWidth: 380 }}>{value}</span>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                onClick={() => inputRef.current?.click()}>
          {busy ? 'Uploading…' : 'Upload image'}
        </button>
      )}

      <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 6 }}>JPG, PNG or WebP, up to 2MB.</p>
      {error && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{error}</p>}
    </>
  );
}

/* ============================= Coupon Submissions (moderation queue) ============================= */

function SubmissionsTab() {
  const { submissions, loading, status, setStatus, approve, reject, refetch } = useCouponSubmissions('pending');
  const [reviewing, setReviewing] = useState(null);

  return (
    <>
      <PageHeader
        title="Coupon Submissions"
        subtitle={loading ? 'Loading…' : `${submissions.length} ${status} submission${submissions.length === 1 ? '' : 's'}`}
        action={
          <select className="form-input form-select" style={{ width: 'auto', padding: '8px 32px 8px 12px' }}
            value={status} onChange={e => setStatus(e.target.value)}>
            <option value="pending">Pending review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
        }
      />

      <div className="ds-panel">
        <div className="ds-panel-body-flush" style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead>
              <tr>
                <th>Submission</th>
                <th>Merchant</th>
                <th>Submitted by</th>
                <th>Discount</th>
                <th>Code</th>
                <th>When</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}><div style={{ padding: 28, textAlign: 'center' }}><div className="spinner" /></div></td></tr>
              ) : submissions.length === 0 ? (
                <tr><td colSpan={7}><EmptyState icon={<i className="bi bi-inbox" />} title={`No ${status} submissions`} subtitle={status === 'pending' ? 'When users submit coupons, they\'ll appear here for review.' : ''} /></td></tr>
              ) : submissions.map(s => (
                <tr key={s.id}>
                  <td>
                    <p style={{ fontWeight: 600, color: '#0F1225' }}>{s.title}</p>
                    {s.description && <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 3, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.description}</p>}
                  </td>
                  <td style={{ fontSize: 13 }}>{s.merchant?.name || <span style={{ color: '#8B94A8' }}>—</span>}</td>
                  <td style={{ fontSize: 12, color: '#4A5568' }}>
                    {s.submitted_by ? (
                      <>
                        <p>{s.submitted_by.name}</p>
                        <p style={{ fontSize: 11, color: '#8B94A8' }}>{s.submitted_by.email}</p>
                      </>
                    ) : '—'}
                  </td>
                  <td><span className="badge badge-gradient">{s.discount_value}{s.discount_type === 'percent' ? '%' : s.discount_type === 'fixed' ? '$' : ''} OFF</span></td>
                  <td style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{s.shared_code || <span style={{ color: '#8B94A8' }}>—</span>}</td>
                  <td style={{ fontSize: 12, color: '#8B94A8' }}>{new Date(s.created_at).toLocaleDateString()}</td>
                  <td>
                    {s.submission_status === 'pending' ? (
                      <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => setReviewing(s)}>Review</button>
                    ) : (
                      <span className={`badge ${s.submission_status === 'approved' ? 'badge-green' : 'badge-red'}`}>
                        {s.submission_status === 'approved' ? '✓ Approved' : '✕ Rejected'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reviewing && (
        <SubmissionReviewModal
          submission={reviewing}
          onClose={() => setReviewing(null)}
          onApprove={async (payload) => { await approve(reviewing.id, payload); setReviewing(null); }}
          onReject={async (reason)  => { await reject(reviewing.id, reason);   setReviewing(null); }}
        />
      )}
    </>
  );
}

function SubmissionReviewModal({ submission, onClose, onApprove, onReject }) {
  const [mode, setMode] = useState(null); // 'approve' | 'reject' | null
  const [priority, setPriority] = useState(0);
  const [isFeatured, setIsFeatured] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await onApprove({ priority: Number(priority) || 0, is_featured: isFeatured, notes: notes || undefined });
    } catch (err) { setError(err.response?.data?.message || 'Approval failed'); setSubmitting(false); }
  };
  const handleReject = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError('A reason is required.'); return; }
    setSubmitting(true); setError('');
    try {
      await onReject(reason);
    } catch (err) { setError(err.response?.data?.message || 'Rejection failed'); setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Review Submission</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Detail block */}
          <div style={{ background: '#F7F8FB', borderRadius: 12, padding: 18, marginBottom: 18 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B94A8', marginBottom: 6 }}>Coupon</p>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#0F1225', marginBottom: 4 }}>{submission.title}</p>
            <p style={{ fontSize: 13, color: '#4A5568' }}>{submission.merchant?.name || 'New merchant'}</p>
            {submission.description && <p style={{ fontSize: 13, color: '#4A5568', marginTop: 8, lineHeight: 1.5 }}>{submission.description}</p>}
            {submission.terms && <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 8, fontStyle: 'italic' }}>Terms: {submission.terms}</p>}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, fontSize: 12 }}>
              <span><strong>Discount:</strong> {submission.discount_value}{submission.discount_type === 'percent' ? '%' : submission.discount_type === 'fixed' ? '$' : ''} {submission.discount_type}</span>
              {submission.shared_code && <span><strong>Code:</strong> <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{submission.shared_code}</code></span>}
              {submission.expires_at && <span><strong>Expires:</strong> {new Date(submission.expires_at).toLocaleDateString()}</span>}
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {!mode && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setMode('reject')}>Reject</button>
              <button className="btn btn-primary"   style={{ flex: 1 }} onClick={() => setMode('approve')}>Approve</button>
            </div>
          )}

          {mode === 'approve' && (
            <form onSubmit={handleApprove}>
              <div className="form-group">
                <label className="form-label">Priority (0–100, higher = more visible)</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  max={100}
                  value={priority}
                  onChange={e => {
                    // Clamp client-side so the user can't post values the
                    // backend will 422 on — input[type=number] doesn't
                    // enforce min/max on its own, only on form submission.
                    const v = e.target.value === '' ? '' : Math.max(0, Math.min(100, Number(e.target.value)));
                    setPriority(v);
                  }}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)} />
                  Feature this coupon on the homepage
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Internal notes (optional)</label>
                <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setMode(null)} disabled={submitting}>Back</button>
                <button type="submit"  className="btn btn-primary"   disabled={submitting}>{submitting ? 'Approving…' : 'Approve & publish'}</button>
              </div>
            </form>
          )}

          {mode === 'reject' && (
            <form onSubmit={handleReject}>
              <div className="form-group">
                <label className="form-label">Reason (visible to submitter) *</label>
                <textarea className="form-input" rows={3} required value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Code didn't work at checkout / merchant doesn't exist / duplicate of existing offer" />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setMode(null)} disabled={submitting}>Back</button>
                <button type="submit"  className="btn btn-danger"    disabled={submitting}>{submitting ? 'Rejecting…' : 'Reject submission'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================= Affiliate Feeds ============================= */

function AffiliateFeedsTab() {
  const { feeds, networks, loading, createFeed, updateFeed, deleteFeed, syncFeed } = useAffiliateFeeds();
  const [showAdd, setShowAdd]     = useState(false);
  const [editing, setEditing]     = useState(null);
  const [syncing, setSyncing]     = useState(null);
  const [deleteId, setDeleteId]   = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const handleSync = async (feed) => {
    setSyncing(feed.id); setSyncResult(null);
    try {
      const result = await syncFeed(feed.id, false);
      setSyncResult({ feed: feed.name, ...result });
    } catch (err) {
      setSyncResult({ feed: feed.name, error: err.response?.data?.error || 'Sync failed' });
    } finally { setSyncing(null); }
  };

  return (
    <>
      <PageHeader
        title="Affiliate Feeds"
        subtitle={loading ? 'Loading…' : `${feeds.length} configured · ${feeds.filter(f => f.is_active).length} active`}
        action={<button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Feed</button>}
      />

      {syncResult && (
        <div className={`alert ${syncResult.error ? 'alert-danger' : 'alert-success'}`} style={{ marginBottom: 16 }}>
          <strong>{syncResult.feed}:</strong>{' '}
          {syncResult.error
            ? syncResult.error
            : `imported ${syncResult.imported}, updated ${syncResult.updated}, skipped ${syncResult.skipped}`}
          <button onClick={() => setSyncResult(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
      )}

      <div className="ds-panel">
        <div className="ds-panel-body-flush" style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead>
              <tr>
                <th>Feed</th>
                <th>Network</th>
                <th>Status</th>
                <th>Last sync</th>
                <th style={{ textAlign: 'right' }}>Imported</th>
                <th style={{ textAlign: 'right' }}>Updated</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}><div style={{ padding: 28, textAlign: 'center' }}><div className="spinner" /></div></td></tr>
              ) : feeds.length === 0 ? (
                <tr><td colSpan={7}><EmptyState icon={<i className="bi bi-link-45deg" />} title="No feeds configured" subtitle="Add your first affiliate network to start syncing deals." /></td></tr>
              ) : feeds.map(f => (
                <tr key={f.id}>
                  <td>
                    <p style={{ fontWeight: 600, color: '#0F1225' }}>{f.name}</p>
                    <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 2 }}>#{f.id}</p>
                  </td>
                  <td><span className="badge" style={{ background: '#F0F2F8', color: '#0F1225', textTransform: 'capitalize' }}>{f.network}</span></td>
                  <td>
                    <span className="badge" style={f.is_active
                      ? { background: 'var(--success-bg)', color: 'var(--success)' }
                      : { background: '#F0F2F8', color: '#8B94A8' }}>
                      {f.is_active ? '● Active' : '○ Inactive'}
                    </span>
                    {f.last_status === 'failed' && <span className="badge badge-red" style={{ marginLeft: 6 }}>⚠ failed</span>}
                    {f.last_status === 'partial' && <span className="badge badge-yellow" style={{ marginLeft: 6 }}>partial</span>}
                  </td>
                  <td style={{ fontSize: 12, color: '#4A5568' }}>
                    {f.last_synced_at ? new Date(f.last_synced_at).toLocaleString() : <span style={{ color: '#8B94A8' }}>Never</span>}
                    {f.last_error && (
                      <p style={{ fontSize: 11, color: '#E53E3E', marginTop: 2, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.last_error}>
                        {f.last_error}
                      </p>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{f.last_imported_count || 0}</td>
                  <td style={{ textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{f.last_updated_count || 0}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="ds-inline-btn" onClick={() => handleSync(f)} disabled={syncing === f.id || !f.is_active}
                        title={f.is_active ? '' : 'Activate the feed first'}>
                        {syncing === f.id ? 'Syncing…' : 'Sync now'}
                      </button>
                      <button className="ds-inline-btn" onClick={() => setEditing(f)}>Edit</button>
                      <button className="ds-inline-btn danger" onClick={() => setDeleteId(f.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!deleteId}
        title="Remove Feed"
        message="The feed will be removed. Coupons already synced will remain in the database."
        danger confirmLabel="Remove"
        onConfirm={async () => { await deleteFeed(deleteId); setDeleteId(null); }}
        onCancel={() => setDeleteId(null)}
      />

      {showAdd  && <AffiliateFeedFormModal networks={networks} onClose={() => setShowAdd(false)} onSave={createFeed} />}
      {editing && <AffiliateFeedFormModal networks={networks} feed={editing} onClose={() => setEditing(null)} onSave={(data) => updateFeed(editing.id, data)} />}
    </>
  );
}

function AffiliateFeedFormModal({ feed, networks, onClose, onSave }) {
  const isEdit = !!feed;
  const [network, setNetwork] = useState(feed?.network || networks[0] || 'awin');
  const [name, setName]       = useState(feed?.name || '');
  const [credsJson, setCredsJson] = useState('{\n  \n}');
  const [isActive, setIsActive] = useState(feed?.is_active ?? false);
  const [country, setCountry] = useState('US');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Hint text per network so admins know what credential keys to put in the JSON.
  const credHint = {
    awin:      'access_token (OAuth2 bearer), publisher_id',
    cj:        'access_token (PAT), company_id, website_id',
    impact:    'account_sid, auth_token (HTTP Basic)',
    rakuten:   'client_id, client_secret, username, password, sid',
    skimlinks: 'api_key, publisher_id',
  }[network];

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    let credentials;
    try { credentials = JSON.parse(credsJson); }
    catch { setError('Credentials JSON is invalid.'); setSaving(false); return; }

    const payload = isEdit
      ? { name, is_active: isActive, ...(credsJson.trim() && credsJson.trim() !== '{}' ? { credentials } : {}) }
      : { network, name, credentials, is_active: isActive, config: { country } };

    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join('. ') : err.response?.data?.message || 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Feed' : 'Add Affiliate Feed'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Network *</label>
              <select className="form-input form-select" value={network} onChange={e => setNetwork(e.target.value)} disabled={isEdit}>
                {networks.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              {isEdit && <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 4 }}>Network can't be changed after creation.</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Display name *</label>
              <input className="form-input" required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Awin US (main account)" />
            </div>
            <div className="form-group">
              <label className="form-label">Credentials (JSON) {!isEdit && '*'}</label>
              <textarea
                className="form-input"
                rows={6}
                value={credsJson}
                onChange={e => setCredsJson(e.target.value)}
                placeholder={`{\n  "key": "value"\n}`}
                style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12 }}
              />
              <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 4 }}>
                Required keys for <strong style={{ textTransform: 'capitalize' }}>{network}</strong>: {credHint}
                {isEdit && <span> · Leave empty <code>{'{}'}</code> to keep existing credentials.</span>}
              </p>
            </div>
            {!isEdit && (
              <div className="form-group">
                <label className="form-label">Country</label>
                <select className="form-input form-select" value={country} onChange={e => setCountry(e.target.value)}>
                  <option value="US">US</option>
                  <option value="UK">UK</option>
                  <option value="CA">CA</option>
                  <option value="AU">AU</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                Active (will run on the daily 03:00 UTC scheduler)
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit"  className="btn btn-primary"   disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add feed')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================= Modals ============================= */

function CouponFormModal({ coupon, onClose }) {
  const isEdit = !!coupon;
  const { merchants: merchantList = [] } = useAdminMerchants();
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data?.data || [])).catch(() => {});
  }, []);
  // Today / +5 years bounds for the date inputs so the picker visually
  // restricts the admin from choosing nonsense dates. The backend also
  // validates these, but min/max on the input gives instant feedback.
  const today = new Date().toISOString().slice(0, 10);
  const fiveYearsOut = new Date(Date.now() + 5 * 365 * 86400 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState(() => ({
    title:          coupon?.title          ?? '',
    description:    coupon?.description    ?? '',
    merchant_id:    coupon?.merchant_id    ?? '',
    category_id:    coupon?.category_id    ?? '',
    discount_type:  coupon?.discount_type  ?? 'percent',
    discount_value: coupon?.discount_value ?? '',
    code_type:      coupon?.code_type      ?? 'shared',
    shared_code:    coupon?.shared_code    ?? '',
    image_url:      coupon?.image_url      ?? '',
    starts_at:      coupon?.starts_at      ? coupon.starts_at.slice(0, 10)  : '',
    expires_at:     coupon?.expires_at     ? coupon.expires_at.slice(0, 10) : '',
    priority:       coupon?.priority       ?? 0,
    is_active:      coupon?.is_active      ?? true,
    is_featured:    coupon?.is_featured    ?? false,
  }));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError]   = useState('');
  const f = k => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  // What the single discount_value column means depends on discount_type, so
  // the field has to say so. It previously read "Value *  e.g. 20" whichever
  // type was selected, and happily accepted 110 for a percentage.
  const DISCOUNT_SHAPES = {
    percent: { label: 'Value (%)', placeholder: 'e.g. 20',    step: '1',    max: 100, used: true,
               hint: 'Percentage off, up to 100.' },
    fixed:   { label: 'Value ($)', placeholder: 'e.g. 20.00', step: '0.01', max: undefined, used: true,
               hint: 'Amount off, in dollars.' },
    freebie: { label: 'Value',     placeholder: 'Not used',   step: '1',    max: undefined, used: false,
               hint: 'A freebie carries no number — name the free item in the description.' },
    bogo:    { label: 'Value',     placeholder: 'Not used',   step: '1',    max: undefined, used: false,
               hint: 'Put the buy-one-get-one terms in the description and terms fields.' },
    bundle:  { label: 'Value',     placeholder: 'Not used',   step: '1',    max: undefined, used: false,
               hint: 'Describe what the bundle contains in the description and terms fields.' },
  };
  const shape = DISCOUNT_SHAPES[form.discount_type] ?? DISCOUNT_SHAPES.percent;

  // Keep the value consistent with the type just chosen. Types that carry no
  // number are sent as 0 (the API still requires the field); coming back from
  // one of those clears it; and switching to percent clamps to 100.
  const onDiscountTypeChange = (e) => {
    const type = e.target.value;
    setForm(p => {
      const s = DISCOUNT_SHAPES[type];
      const next = { ...p, discount_type: type };
      if (s && !s.used)                                    next.discount_value = 0;
      else if (Number(p.discount_value) === 0)             next.discount_value = '';
      else if (type === 'percent' && Number(p.discount_value) > 100) next.discount_value = 100;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError(''); setErrors({});

    // Strip empty optional fields so backend's nullable rules pass cleanly.
    const payload = Object.fromEntries(
      Object.entries(form).filter(([_, v]) => v !== '' && v != null)
    );
    if (payload.discount_value !== undefined) payload.discount_value = Number(payload.discount_value);
    if (payload.merchant_id    !== undefined) payload.merchant_id    = Number(payload.merchant_id);
    if (payload.priority       !== undefined) payload.priority       = Math.max(0, Math.min(100, Number(payload.priority) || 0));

    try {
      if (isEdit) await api.put(`/admin/coupons/${coupon.id}`, payload);
      else        await api.post('/admin/coupons', payload);
      onClose();
    } catch (err) {
      const fields = err.response?.data?.errors;
      if (fields) setErrors(Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v[0]])));
      else        setError(err.response?.data?.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? `Edit Coupon #${coupon.id}` : 'New Coupon'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" required {...f('title')} placeholder="e.g. 20% off all orders" />
              {errors.title && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{errors.title}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={2} {...f('description')} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Merchant *</label>
                <select className="form-input form-select" required {...f('merchant_id')}>
                  <option value="">Select a merchant…</option>
                  {merchantList.map(m => (
                    <option key={m.id} value={m.id}>{m.name} (#{m.id})</option>
                  ))}
                </select>
                {errors.merchant_id && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{errors.merchant_id}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-input form-select" {...f('category_id')}>
                  <option value="">(optional)</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Discount Type *</label>
                <select className="form-input form-select" value={form.discount_type} onChange={onDiscountTypeChange}>
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed $</option>
                  <option value="freebie">Freebie</option>
                  <option value="bogo">BOGO</option>
                  <option value="bundle">Bundle</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{shape.label}{shape.used ? ' *' : ''}</label>
                <input
                  className="form-input"
                  type="number"
                  step={shape.step}
                  min="0"
                  max={shape.max}
                  required={shape.used}
                  disabled={!shape.used}
                  placeholder={shape.placeholder}
                  {...f('discount_value')}
                />
                <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 4 }}>{shape.hint}</p>
                {errors.discount_value && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{errors.discount_value}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Code Type *</label>
                <select className="form-input form-select" {...f('code_type')}>
                  <option value="shared">Shared code</option>
                  <option value="unique">Unique codes</option>
                  <option value="no_code">No code</option>
                  <option value="affiliate_link">Affiliate link</option>
                </select>
              </div>
              {form.code_type === 'shared' && (
                <div className="form-group">
                  <label className="form-label">Promo Code *</label>
                  <input className="form-input" required {...f('shared_code')} placeholder="SAVE20" style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: 1 }} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Coupon image</label>
                <ImageUploadField
                  value={form.image_url}
                  onChange={url => setForm(p => ({ ...p, image_url: url }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Priority (0–100)</label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  max={100}
                  value={form.priority ?? 0}
                  onChange={e => {
                    // HTML5's min/max on type=number is advisory — the
                    // browser only enforces it on the spinner, not on
                    // typed values. Clamp on every keystroke so the
                    // field can't display 10000000000000000 even
                    // momentarily, which is what Ananya's QA flagged.
                    const raw = e.target.value;
                    if (raw === '') { setForm(p => ({ ...p, priority: '' })); return; }
                    const n = Number(raw);
                    if (Number.isNaN(n)) return;
                    setForm(p => ({ ...p, priority: Math.max(0, Math.min(100, n)) }));
                  }}
                />
                {errors.priority && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{errors.priority}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Starts at</label>
                <input className="form-input" type="date" min={today} {...f('starts_at')} />
                {errors.starts_at && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{errors.starts_at}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Expires at</label>
                <input className="form-input" type="date"
                  min={form.starts_at || today}
                  max={fiveYearsOut}
                  {...f('expires_at')} />
                {errors.expires_at && <p style={{ color: '#E53E3E', fontSize: 12, marginTop: 4 }}>{errors.expires_at}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 4, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={form.is_active}   onChange={e => setForm(p => ({ ...p, is_active:   e.target.checked }))} />
                Active
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={form.is_featured} onChange={e => setForm(p => ({ ...p, is_featured: e.target.checked }))} />
                Featured
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create coupon')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function BulkImportCouponsModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState('');
  const [result, setResult] = useState(null);

  // `accept` only filters the picker's default view — the user can switch it
  // to "All files" and choose anything — so check the actual selection before
  // spending a round trip on it.
  const looksLikeCsv = (f) =>
    /\.(csv|txt)$/i.test(f.name) ||
    ['text/csv', 'text/plain', 'application/vnd.ms-excel', ''].includes(f.type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Pick a CSV file first.'); return; }
    if (!looksLikeCsv(file)) {
      setError(`“${file.name}” is not a CSV file. Please upload a .csv file.`);
      return;
    }
    setSubmitting(true); setError(''); setResult(null);

    const fd = new FormData();
    fd.append('file', file);

    try {
      const { data } = await api.post('/admin/coupons/bulk-import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (err) {
      // Field errors first: Laravel's envelope message is a generic
      // "Validation failed", and checking it first hid the one line that
      // actually said what was wrong with the file.
      const fieldErrors = err.response?.data?.errors;
      setError(
        fieldErrors?.file?.[0]
        || (fieldErrors && Object.values(fieldErrors).flat()[0])
        || err.response?.data?.message
        || 'Import failed.'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(!!result)}>
      <div className="modal-box" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Bulk import coupons (CSV)</span>
          <button className="modal-close" onClick={() => onClose(!!result)}>×</button>
        </div>
        <div className="modal-body">
          {!result ? (
            <form onSubmit={handleSubmit}>
              <div style={{ background: '#F7F8FC', borderRadius: 10, padding: 16, marginBottom: 18, fontSize: 13, color: '#4A5568', lineHeight: 1.6 }}>
                <p style={{ fontWeight: 700, color: '#0F1225', marginBottom: 8 }}>CSV format</p>
                <p>First row = header. Required columns:</p>
                <code style={{ display: 'block', background: '#fff', padding: '8px 12px', borderRadius: 6, marginTop: 8, fontSize: 11 }}>
                  merchant_name, title, discount_type, discount_value
                </code>
                <p style={{ marginTop: 8 }}>Optional: <code>merchant_url, description, terms, code_type, code, image_url, starts_at, expires_at</code>.</p>
                <p style={{ marginTop: 8, fontSize: 12, color: '#8B94A8' }}>
                  Merchants are matched by slug; new ones are auto-created with <code>is_verified=false</code>. Max 10MB.
                </p>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              <div className="form-group">
                <label className="form-label">CSV file *</label>
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  required
                  onChange={e => setFile(e.target.files?.[0] || null)}
                  className="form-input"
                  style={{ padding: 8 }}
                />
                {file && <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 6 }}>{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => onClose(false)} disabled={submitting}>Cancel</button>
                <button type="submit"  className="btn btn-primary"   disabled={submitting || !file}>
                  {submitting ? 'Importing…' : 'Import'}
                </button>
              </div>
            </form>
          ) : (
            <div>
              <div className={`alert ${result.skipped > 0 && result.imported === 0 ? 'alert-danger' : 'alert-success'}`}>
                <strong>{result.message || `Imported ${result.imported}, skipped ${result.skipped}.`}</strong>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
                <div style={{ background: '#F7F8FC', padding: 16, borderRadius: 10 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B94A8' }}>Imported</p>
                  <p style={{ fontSize: 26, fontWeight: 800, color: '#0F9D6E', marginTop: 4 }}>{result.imported}</p>
                </div>
                <div style={{ background: '#F7F8FC', padding: 16, borderRadius: 10 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8B94A8' }}>Skipped</p>
                  <p style={{ fontSize: 26, fontWeight: 800, color: result.skipped > 0 ? '#DC2626' : '#0F1225', marginTop: 4 }}>{result.skipped}</p>
                </div>
              </div>

              {result.errors?.length > 0 && (
                <details style={{ marginTop: 18, background: '#FDECEA', border: '1px solid #FCA5A5', borderRadius: 10, padding: 12 }}>
                  <summary style={{ fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{result.errors.length} error{result.errors.length === 1 ? '' : 's'} — click to see</summary>
                  <ul style={{ marginTop: 10, paddingLeft: 20, fontSize: 12, color: '#7F1D1D', lineHeight: 1.6 }}>
                    {result.errors.map((e, i) => <li key={i}><code>{e}</code></li>)}
                  </ul>
                </details>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                <button className="btn btn-primary" onClick={() => onClose(true)}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MerchantFormModal({ merchant, onClose }) {
  const isEdit = !!merchant;
  const [form, setForm] = useState({
    name:              merchant?.name              ?? '',
    website:           merchant?.website           ?? '',
    affiliate_network: merchant?.affiliate_network ?? 'none',
    affiliate_id:      merchant?.affiliate_id      ?? '',
    commission_pct:    merchant?.commission_pct    ?? '',
    contact_email:     merchant?.contact_email     ?? '',
    category_id:       merchant?.category_id       ?? '',
    logo_url:          merchant?.logo_url          ?? '',
    description:       merchant?.description       ?? '',
    is_active:         merchant?.is_active         ?? true,
    is_featured:       merchant?.is_featured       ?? false,
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const f = k => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data?.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    const payload = { ...form };
    // Backend rules treat empty strings as missing/invalid for nullable
    // fields — strip them so the validator sees real "not set" values.
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
    if (payload.commission_pct !== undefined) payload.commission_pct = Number(payload.commission_pct);
    if (payload.category_id    !== undefined) payload.category_id    = Number(payload.category_id);
    try {
      if (isEdit) await api.patch(`/admin/merchants/${merchant.id}`, payload);
      else        await api.post('/admin/merchants', payload);
      onClose();
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      if (fieldErrors) setError(Object.values(fieldErrors).flat()[0] || 'Failed to save');
      else             setError(err.response?.data?.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? `Edit · ${merchant.name}` : 'Add Merchant'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label className="form-label">Business Name *</label><input className="form-input" required {...f('name')} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-input form-select" value={form.category_id || ''} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Website</label><input className="form-input" type="url" placeholder="https://example.com" {...f('website')} /></div>
            </div>
            <div className="form-group"><label className="form-label">Logo URL</label><input className="form-input" type="url" placeholder="https://…/logo.png" {...f('logo_url')} /></div>
            <div className="form-group"><label className="form-label">Contact Email</label><input className="form-input" type="email" {...f('contact_email')} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Affiliate Network</label>
                <select className="form-input form-select" {...f('affiliate_network')}>
                  <option value="none">None (Direct)</option>
                  <option value="cj">CJ Affiliate</option>
                  <option value="rakuten">Rakuten</option>
                  <option value="impact">Impact</option>
                  <option value="shareasale">ShareASale</option>
                  <option value="amazon">Amazon</option>
                  <option value="awin">Awin</option>
                  <option value="skimlinks">Skimlinks</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Commission %</label><input className="form-input" type="number" min="0" max="100" step="0.5" {...f('commission_pct')} /></div>
            </div>
            <div className="form-group"><label className="form-label">Affiliate ID / Publisher ID</label><input className="form-input" placeholder="Optional — your ID with the network" {...f('affiliate_id')} /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={3} {...f('description')} /></div>
            <div style={{ display: 'flex', gap: 18, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={!!form.is_active}   onChange={e => setForm(p => ({ ...p, is_active:   e.target.checked }))} /> Active (visible publicly)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={!!form.is_featured} onChange={e => setForm(p => ({ ...p, is_featured: e.target.checked }))} /> Featured
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add Merchant')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function AppClientFormModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ app_id: '', app_name: '', website_url: '', brand_color: '#6B2FD9', contact_email: '', plan: 'free', allowed_origins: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const f = k => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { data } = await api.post('/admin/app-clients', form);
      setCreated(data);
      onCreated?.();
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      if (fieldErrors) {
        setError(Object.values(fieldErrors).flat()[0] || 'Failed to create');
      } else {
        setError(err.response?.data?.message || 'Failed to create');
      }
      setSaving(false);
    }
  };

  const copyKey = () => {
    if (created?.api_key) navigator.clipboard.writeText(created.api_key);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Register App Client</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {created ? (
            <div>
              <div className="alert alert-success">
                ✓ App client created. Both values are also available later under <strong>Credentials</strong>.
              </div>
              <div style={{ fontSize: 13, color: '#8B94A8', marginBottom: 14, fontFamily: 'ui-monospace, monospace' }}>
                app_id: <span style={{ color: '#0F1225', fontWeight: 700 }}>{created.data?.app_id}</span>
              </div>
              <SecretField label="X-API-Key" value={created.api_key || ''} />
              <SecretField label="X-API-Secret" value={created.api_secret || ''} monoColor="#D946EF" />
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" onClick={copyKey} style={{ flex: 1 }}><i className="bi bi-clipboard" /> Copy API key</button>
                <button className="btn btn-primary" onClick={onClose} style={{ flex: 1 }}>Done</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-group"><label className="form-label">App ID (slug) *</label><input className="form-input" required pattern="[a-z0-9_-]+" placeholder="e.g. fundmesmart" {...f('app_id')} style={{ fontFamily: 'ui-monospace, monospace' }} /></div>
              <div className="form-group"><label className="form-label">App Name *</label><input className="form-input" required {...f('app_name')} /></div>
              <div className="form-group"><label className="form-label">Website</label><input className="form-input" type="url" {...f('website_url')} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group"><label className="form-label">Brand Color</label><input className="form-input" type="color" {...f('brand_color')} style={{ padding: 4, height: 44 }} /></div>
                <div className="form-group"><label className="form-label">Plan</label><select className="form-input form-select" {...f('plan')}><option value="free">Free</option><option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option></select></div>
              </div>
              <OriginsField value={form.allowed_origins} onChange={(v) => setForm(p => ({ ...p, allowed_origins: v }))} />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create App'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================= System Health ============================= */

const HEALTH_COLORS = {
  healthy:  { bg: '#DCFCE7', fg: '#166534', label: '✓ Healthy' },
  degraded: { bg: '#FEF3C7', fg: '#92400E', label: '⚠ Degraded' },
  error:    { bg: '#FEE2E2', fg: '#991B1B', label: '✗ Error' },
};

function HealthTab() {
  const { health, loading, refresh } = useSystemHealth();
  if (loading) return <LoadingPage />;
  if (!health) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-heart-pulse" />} title="Health probe failed" subtitle="The /admin/health endpoint did not respond." /></div></div>;
  const overall = HEALTH_COLORS[health.status] || HEALTH_COLORS.error;

  return (
    <>
      <PageHeader
        title="System Health"
        subtitle={`Overall: ${health.status} · checked ${health.checked_at ? new Date(health.checked_at).toLocaleTimeString() : '—'}`}
        action={<button className="btn btn-secondary" onClick={refresh}>↻ Refresh</button>}
      />

      <div style={{ background: overall.bg, color: overall.fg, padding: '14px 18px', borderRadius: 12, fontWeight: 700, fontSize: 16, marginBottom: 18 }}>
        {overall.label} — platform status: {health.status}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard label="Pending jobs" value={(health.counters?.pending_jobs ?? '—').toLocaleString?.() || '—'} icon={<i className="bi bi-hourglass-split" />} />
        <StatCard label="Failed jobs"  value={(health.counters?.failed_jobs  ?? '—').toLocaleString?.() || '—'} icon={<i className="bi bi-exclamation-octagon" />} />
        <StatCard label="Unverified users" value={(health.counters?.unverified_users ?? '—').toLocaleString?.() || '—'} icon={<i className="bi bi-envelope-exclamation" />} />
        <StatCard label="Total users"  value={(health.counters?.total_users  ?? '—').toLocaleString?.() || '—'} icon={<i className="bi bi-people" />} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {Object.entries(health.checks || {}).map(([name, c]) => {
          const color = HEALTH_COLORS[c.status] || HEALTH_COLORS.error;
          return (
            <div key={name} className="ds-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ fontWeight: 700, fontSize: 14, textTransform: 'capitalize' }}>{name.replace(/_/g, ' ')}</p>
                <span style={{ padding: '2px 8px', borderRadius: 6, background: color.bg, color: color.fg, fontSize: 11.5, fontWeight: 700 }}>{color.label}</span>
              </div>
              <p style={{ fontSize: 13, color: '#5A6378' }}>{c.detail || '—'}</p>
              {c.latency_ms != null && <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 4 }}>latency: {c.latency_ms}ms</p>}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ============================= Webhooks ============================= */

function WebhooksTab() {
  const { webhooks, availableEvents, loading, refresh } = useWebhooks();
  const { clients } = useAppClients();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [newSecret, setNewSecret] = useState(null);

  const runTest = async (w) => {
    setTesting(w.id); setTestResult(null);
    try {
      const { data } = await api.post(`/admin/webhooks/${w.id}/test`);
      setTestResult({ id: w.id, ...data });
      refresh();
    } catch (err) {
      setTestResult({ id: w.id, success: false, error: err.response?.data?.message || err.message });
    } finally { setTesting(null); }
  };

  const rotate = async (w) => {
    if (!(await confirm({
      title: 'Rotate webhook secret?',
      message: `Receivers signed with the old secret for “${w.name}” will start rejecting deliveries until they update. Continue?`,
      danger: true,
      confirmLabel: 'Rotate',
    }))) return;
    try {
      const { data } = await api.post(`/admin/webhooks/${w.id}/rotate-secret`);
      setNewSecret({ name: w.name, secret: data.secret });
      refresh();
    } catch (err) {
      toast.error('Rotation failed', err.response?.data?.message);
    }
  };

  const remove = async (w) => {
    if (!(await confirm({
      title: 'Delete webhook?',
      message: `“${w.name}” will be removed. Pending deliveries will not be sent.`,
      danger: true,
      confirmLabel: 'Delete',
    }))) return;
    try {
      await api.delete(`/admin/webhooks/${w.id}`);
      refresh();
      toast.success('Webhook deleted');
    } catch (err) { toast.error('Delete failed'); }
  };

  if (loading) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="Webhooks"
        subtitle="Outbound event delivery to partner integrations"
        action={<button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Webhook</button>}
      />

      {webhooks.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-diagram-3" />} title="No webhooks yet" subtitle="Register a URL to receive event notifications from PerkVora." /></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {webhooks.map(w => (
            <div key={w.id} className="ds-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 700, fontSize: 15 }}>{w.name}</p>
                  <p style={{ fontSize: 12, color: '#8B94A8', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{w.endpoint_url}</p>
                </div>
                <span className={`badge ${w.is_active ? 'badge-green' : 'badge-red'}`}>{w.is_active ? '● Active' : '● Inactive'}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, fontSize: 12, marginBottom: 12 }}>
                <div><p style={statLabel}>Tenant</p><p style={statVal}>{w.app_client?.app_id || '—'}</p></div>
                <div><p style={statLabel}>Success</p><p style={{ ...statVal, color: '#166534' }}>{w.success_count?.toLocaleString() || 0}</p></div>
                <div><p style={statLabel}>Failure</p><p style={{ ...statVal, color: '#991B1B' }}>{w.failure_count?.toLocaleString() || 0}</p></div>
                <div><p style={statLabel}>Last fired</p><p style={statVal}>{w.last_fired_at ? new Date(w.last_fired_at).toLocaleString() : 'never'}</p></div>
                <div><p style={statLabel}>Secret</p><p style={{ ...statVal, fontFamily: 'ui-monospace, monospace' }}>{w.secret_tail || '—'}</p></div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <p style={statLabel}>Events</p>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                  {(w.events || []).map(e => <span key={e} style={{ padding: '2px 8px', background: e === '*' ? '#FEF3C7' : '#EBE0FF', color: e === '*' ? '#92400E' : '#6B2FD9', borderRadius: 6, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{e}</span>)}
                </div>
              </div>

              {w.last_status === 'error' && w.last_error && (
                <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                  ⚠ Last error: {friendlyWebhookError(w.last_error)}
                  <details style={{ marginTop: 4, fontSize: 11, opacity: 0.7 }}>
                    <summary style={{ cursor: 'pointer' }}>Show technical details</summary>
                    <code style={{ display: 'block', marginTop: 4, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{w.last_error}</code>
                  </details>
                </div>
              )}

              {testResult?.id === w.id && (
                <div style={{ padding: '8px 12px', background: testResult.success ? '#DCFCE7' : '#FEE2E2', color: testResult.success ? '#166534' : '#991B1B', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                  {testResult.success ? `✓ ${testResult.response_status} in ${testResult.duration_ms}ms` : `✗ ${testResult.error || `HTTP ${testResult.response_status}`}`}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => runTest(w)} disabled={testing === w.id}>{testing === w.id ? '…' : <><i className="bi bi-lightning-charge-fill" /> Test</>}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => rotate(w)}><i className="bi bi-arrow-repeat" /> Rotate Secret</button>
                <button className="btn btn-secondary btn-sm" onClick={() => remove(w)} style={{ color: '#991B1B' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <WebhookFormModal
          clients={clients}
          availableEvents={availableEvents}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); refresh(); }}
        />
      )}

      {newSecret && (
        <div className="modal-overlay" onClick={() => setNewSecret(null)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">New webhook secret</span>
              <button className="modal-close" onClick={() => setNewSecret(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: '#5A6378', marginBottom: 10 }}>
                Save this secret for <strong>{newSecret.name}</strong> — it will only be shown once.
                Hand it to whoever maintains the receiver and ask them to update their HMAC config.
              </p>
              <pre style={{
                background: '#0E0F2E', color: '#C4C7DC', borderRadius: 10, padding: 14,
                fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13,
                wordBreak: 'break-all', whiteSpace: 'pre-wrap',
              }}>{newSecret.secret}</pre>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => { navigator.clipboard?.writeText(newSecret.secret); toast.success('Copied'); }}
                >Copy</button>
                <button className="btn btn-primary" onClick={() => setNewSecret(null)}>I saved it</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmNode}
    </>
  );
}

const statLabel = { fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' };
const statVal   = { fontWeight: 700, marginTop: 2, fontSize: 13 };

function WebhookFormModal({ clients, availableEvents, onClose, onCreated }) {
  const [form, setForm] = useState({ app_client_id: clients[0]?.id || '', name: '', endpoint_url: '', events: [], is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const toggleEvent = (e) => {
    setForm(p => {
      const next = new Set(p.events);
      if (next.has(e)) next.delete(e); else next.add(e);
      return { ...p, events: Array.from(next) };
    });
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault(); setSaving(true); setError('');
    try {
      const { data } = await api.post('/admin/webhooks', form);
      setCreated(data);
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      setError(fieldErrors ? Object.values(fieldErrors).flat()[0] : (err.response?.data?.message || 'Save failed'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{created ? 'Webhook Created' : 'New Webhook'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {created ? (
            <div>
              <div className="alert alert-success">✓ Webhook created. Save the secret now — it will not be shown again.</div>
              <div style={{ background: '#0E0F2E', color: '#D946EF', borderRadius: 12, padding: 16, fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all', marginBottom: 16 }}>
                <strong>secret:</strong> {created.secret}
              </div>
              <button className="btn btn-primary btn-full" onClick={onCreated}>Done</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-danger">{error}</div>}

              <div className="form-group">
                <label className="form-label">Tenant</label>
                <select className="form-input form-select" value={form.app_client_id} onChange={(e) => setForm(p => ({ ...p, app_client_id: e.target.value }))}>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.app_name} ({c.app_id})</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. partner-api-coupon-sync" />
              </div>

              <div className="form-group">
                <label className="form-label">Endpoint URL</label>
                <input className="form-input" type="url" required value={form.endpoint_url} onChange={(e) => setForm(p => ({ ...p, endpoint_url: e.target.value }))} placeholder="https://partner.example.com/perkvora/webhook" />
              </div>

              <div className="form-group">
                <label className="form-label">Events (* = all)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', background: '#FAFBFD', borderRadius: 8, border: '1px solid #E4E7F0' }}>
                  {availableEvents.map(e => {
                    const on = form.events.includes(e);
                    return (
                      <button key={e} type="button" onClick={() => toggleEvent(e)}
                        style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: 11.5, fontFamily: 'ui-monospace, monospace',
                          background: on ? (e === '*' ? '#FEF3C7' : '#EBE0FF') : '#fff',
                          color: on ? (e === '*' ? '#92400E' : '#6B2FD9') : '#5A6378',
                          border: `1px solid ${on ? (e === '*' ? '#FCD34D' : '#D8C8F8') : '#E4E7F0'}`,
                          cursor: 'pointer',
                        }}>{e}</button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || form.events.length === 0}>{saving ? 'Saving…' : 'Create webhook'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================= Funnel ============================= */

function FunnelTab() {
  const { data, loading } = useOnboardingFunnel();
  if (loading) return <LoadingPage />;
  if (!data || !data.stages) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-funnel" />} title="No data" /></div></div>;
  const max = data.stages[0]?.count || 1;

  return (
    <>
      <PageHeader
        title="Onboarding Funnel"
        subtitle="Registered → verified → first redemption → first cashback → first payout"
      />

      {data.biggest_drop?.from && (
        <div style={{ background: '#FEF3C7', color: '#92400E', padding: '14px 18px', borderRadius: 12, marginBottom: 18, fontWeight: 600 }}>
          ⚠ Biggest drop: <strong>{data.biggest_drop.from}</strong> → <strong>{data.biggest_drop.to}</strong> (−{data.biggest_drop.drop_pct}%). This is where to invest activation effort.
        </div>
      )}

      <div className="ds-panel">
        <div className="ds-panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {data.stages.map((s, idx) => {
            const widthPct = (s.count / max) * 100;
            return (
              <div key={s.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <strong>{s.label}</strong>
                  <span style={{ color: '#5A6378', fontSize: 13 }}>
                    {s.count.toLocaleString()} · {s.conversion_pct}% of cohort
                    {s.drop_from_previous_pct != null && <span style={{ marginLeft: 8, color: '#991B1B' }}>(−{s.drop_from_previous_pct}% vs prev)</span>}
                  </span>
                </div>
                <div style={{ height: 24, background: '#F0F2F8', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${widthPct}%`, background: idx === 0 ? '#6B2FD9' : 'linear-gradient(90deg,#6B2FD9,#D946EF)', transition: 'width 0.3s' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ============================= Email Health ============================= */

function EmailHealthTab() {
  const [days, setDays] = useState(30);
  const { data, loading } = useEmailHealth(days);
  if (loading) return <LoadingPage />;
  if (!data) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-envelope" />} title="No data" /></div></div>;

  return (
    <>
      <PageHeader
        title="Email Deliverability"
        subtitle={`Last ${data.window_days}d via ${data.mail_config?.mailer || '—'} · ${data.mail_config?.from || ''}`}
        action={<select className="form-input form-select" value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 140 }}><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option></select>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard label="Total" value={data.total.toLocaleString()} icon={<i className="bi bi-envelope" />} />
        <StatCard label="Sent" value={data.sent.toLocaleString()} icon={<i className="bi bi-check2-circle" />} />
        <StatCard label="Failed" value={data.failed.toLocaleString()} icon={<i className="bi bi-exclamation-triangle" />} />
        <StatCard label="Delivery rate" value={`${data.delivery_rate_pct}%`} icon={<i className="bi bi-bar-chart" />} />
      </div>

      <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 24, marginBottom: 10 }}>By event type</h3>
      <div className="ds-panel" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#FAFBFD' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Event</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Total</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Sent</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Failed</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Fail %</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.by_event || {}).map(([key, row]) => (
                <tr key={key} style={{ borderTop: '1px solid #F0F2F8' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{key}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>{row.total}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: '#166534' }}>{row.sent}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: '#991B1B' }}>{row.failed}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: row.fail_rate_pct > 10 ? '#991B1B' : '#5A6378' }}>{row.fail_rate_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.recent_failures?.length > 0 && (
        <>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 24, marginBottom: 10 }}>Recent failures</h3>
          <div className="ds-panel" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#FAFBFD' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>When</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Event</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Recipient</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_failures.map(f => (
                    <tr key={f.id} style={{ borderTop: '1px solid #F0F2F8' }}>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: '#5A6378' }}>{new Date(f.created_at).toLocaleString()}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>{f.event_key}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12 }}>{f.user_email || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: '#991B1B' }}>{f.failure_reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ============================= Cohorts ============================= */

function CohortsTab() {
  const [metric, setMetric] = useState('redemptions');
  const [months, setMonths] = useState(12);
  const { data, loading } = useCohorts(metric, months);
  if (loading) return <LoadingPage />;
  if (!data) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-graph-up" />} title="No data" /></div></div>;

  const heatColor = (pct) => {
    if (pct >= 50) return { bg: '#166534', fg: '#fff' };
    if (pct >= 30) return { bg: '#22C55E', fg: '#fff' };
    if (pct >= 15) return { bg: '#86EFAC', fg: '#0F1225' };
    if (pct > 0)   return { bg: '#DCFCE7', fg: '#0F1225' };
    return { bg: '#F0F2F8', fg: '#8B94A8' };
  };

  return (
    <>
      <PageHeader
        title="Cohort Retention"
        subtitle={`Group users by signup month, see when they came back to ${metric === 'redemptions' ? 'redeem' : 'earn cashback'}`}
        action={(
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="form-input form-select" value={metric} onChange={(e) => setMetric(e.target.value)}>
              <option value="redemptions">Redemptions</option>
              <option value="cashback">Cashback</option>
            </select>
            <select className="form-input form-select" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
              <option value={6}>6 months</option>
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
            </select>
          </div>
        )}
      />

      {data.cohorts?.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-graph-up" />} title="Not enough cohort data" subtitle="Need at least one signup month worth of data." /></div></div>
      ) : (
        <div className="ds-panel" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#FAFBFD' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Cohort</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 600, textTransform: 'uppercase' }}>Size</th>
                  {Array.from({ length: months + 1 }).map((_, m) => (
                    <th key={m} style={{ padding: '8px 6px', textAlign: 'center', fontSize: 11, color: '#5A6378', fontWeight: 600 }}>M{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.cohorts || []).map(c => (
                  <tr key={c.cohort_month} style={{ borderTop: '1px solid #F0F2F8' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600 }}>{c.cohort_month}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12 }}>{c.size}</td>
                    {c.cells.map(cell => {
                      const color = heatColor(cell.retention_pct);
                      return (
                        <td key={cell.month_offset} title={`${cell.count} users · ${cell.retention_pct}%`} style={{ padding: '6px 4px', textAlign: 'center', background: color.bg, color: color.fg, fontSize: 11, minWidth: 40 }}>
                          {cell.retention_pct > 0 ? `${cell.retention_pct}%` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================= Feature Flags ============================= */

function FeatureFlagsTab() {
  const { flags, loading, refresh } = useFeatureFlags();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const remove = async (f) => {
    if (!(await confirm({ title: 'Delete feature flag?', message: `“${f.key}” will be removed. Any code reading it will fall back to its default. This can't be undone.`, danger: true, confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/admin/feature-flags/${f.id}`); refresh(); toast.success('Flag deleted'); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="Feature Flags"
        subtitle="Master switch + percentage rollout + role/user override. Resolution: override → percentage → master."
        action={<button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Flag</button>}
      />

      {flags.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-flag" />} title="No feature flags yet" subtitle="Create flags to gate experimental features and roll out gradually." /></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {flags.map(f => (
            <div key={f.id} className="ds-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14, fontWeight: 700 }}>{f.key}</span>
                <span className={`badge ${f.is_enabled ? 'badge-green' : 'badge-red'}`}>{f.is_enabled ? '● On' : '● Off'}</span>
                <span style={{ fontSize: 12, color: '#5A6378', fontWeight: 600 }}>{f.rollout_pct}% rollout</span>
                <div style={{ flex: 1 }} />
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(f)}>Edit</button>
                <button className="btn btn-secondary btn-sm" onClick={() => remove(f)} style={{ color: '#991B1B' }}>Delete</button>
              </div>
              <p style={{ fontSize: 14, fontWeight: 600 }}>{f.name}</p>
              {f.description && <p style={{ fontSize: 13, color: '#5A6378', marginTop: 4 }}>{f.description}</p>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {(f.roles || []).map(r => <span key={r} style={{ padding: '2px 8px', background: '#EBE0FF', color: '#6B2FD9', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>role:{r}</span>)}
                {(f.user_ids || []).map(id => <span key={id} style={{ padding: '2px 8px', background: '#FEF3C7', color: '#92400E', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>user:#{id}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {(showNew || editing) && (
        <FeatureFlagModal flag={editing} onClose={() => { setEditing(null); setShowNew(false); }} onSaved={() => { refresh(); setEditing(null); setShowNew(false); }} />
      )}
      {confirmNode}
    </>
  );
}

function FeatureFlagModal({ flag, onClose, onSaved }) {
  const [form, setForm] = useState({
    key: flag?.key || '',
    name: flag?.name || '',
    description: flag?.description || '',
    is_enabled: flag?.is_enabled ?? false,
    rollout_pct: flag?.rollout_pct ?? 0,
    roles: (flag?.roles || []).join(', '),
    user_ids: (flag?.user_ids || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    const payload = {
      ...form,
      roles: form.roles ? form.roles.split(',').map(r => r.trim()).filter(Boolean) : null,
      user_ids: form.user_ids ? form.user_ids.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n)) : null,
    };
    try {
      if (flag) await api.patch(`/admin/feature-flags/${flag.id}`, payload);
      else      await api.post('/admin/feature-flags', payload);
      onSaved();
    } catch (err) {
      const fe = err.response?.data?.errors;
      setError(fe ? Object.values(fe).flat()[0] : (err.response?.data?.message || 'Save failed'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{flag ? `Edit ${flag.key}` : 'New Feature Flag'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={submit}>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="form-group"><label className="form-label">Key (slug)</label><input className="form-input" required disabled={!!flag} pattern="[a-z0-9_.-]+" value={form.key} onChange={(e) => setForm(p => ({ ...p, key: e.target.value }))} placeholder="e.g. cashback_v2" style={{ fontFamily: 'ui-monospace, monospace' }} /></div>
            <div className="form-group"><label className="form-label">Display name</label><input className="form-input" required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={2} value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} /></div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.is_enabled} onChange={(e) => setForm(p => ({ ...p, is_enabled: e.target.checked }))} />
                <span style={{ fontWeight: 600 }}>Master switch (when off, everyone sees it as off)</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Rollout %: <strong>{form.rollout_pct}%</strong></label>
              <input type="range" min={0} max={100} value={form.rollout_pct} onChange={(e) => setForm(p => ({ ...p, rollout_pct: Number(e.target.value) }))} style={{ width: '100%' }} />
              <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 2 }}>Stable hash bucketing — same user always lands in the same bucket.</p>
            </div>

            <div className="form-group"><label className="form-label">Role overrides (comma-separated)</label><input className="form-input" value={form.roles} onChange={(e) => setForm(p => ({ ...p, roles: e.target.value }))} placeholder="ops, finance" style={{ fontFamily: 'ui-monospace, monospace' }} /></div>
            <div className="form-group"><label className="form-label">User ID overrides (comma-separated)</label><input className="form-input" value={form.user_ids} onChange={(e) => setForm(p => ({ ...p, user_ids: e.target.value }))} placeholder="1, 42, 100" style={{ fontFamily: 'ui-monospace, monospace' }} /></div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (flag ? 'Save changes' : 'Create flag')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================= Broadcasts ============================= */

const CAMPAIGN_STATUS_META = {
  draft:     { label: 'Draft',     bg: '#F1F5F9', fg: '#475569' },
  scheduled: { label: 'Scheduled', bg: '#DBEAFE', fg: '#1E40AF' },
  sending:   { label: 'Sending',   bg: '#FEF3C7', fg: '#92400E' },
  sent:      { label: 'Sent',      bg: '#DCFCE7', fg: '#166534' },
  cancelled: { label: 'Cancelled', bg: '#FEE2E2', fg: '#991B1B' },
};

const EMPTY_BROADCAST_FORM = { subject: '', body: '', segment: { audience: 'opted_in' }, scheduled_at: '' };

function BroadcastsTab() {
  const { rows, loading: listLoading, refresh: refreshList } = useBroadcasts();
  const [confirm, confirmNode]      = useConfirm();
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState(EMPTY_BROADCAST_FORM);
  const [preview, setPreview]       = useState(null);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState('');
  const [result, setResult]         = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_BROADCAST_FORM);
    setPreview(null);
    setError('');
  };

  const loadIntoForm = (campaign) => {
    setEditingId(campaign.id);
    setForm({
      subject:      campaign.subject || '',
      body:         campaign.body || '',
      segment:      campaign.segment_filter || { audience: 'opted_in' },
      scheduled_at: campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : '', // <input type=datetime-local> wants no Z/seconds
    });
    setPreview(null);
    setError('');
    setResult(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildPayload = () => {
    // Strip the local datetime-local string into ISO; backend re-parses.
    const payload = { subject: form.subject, body: form.body, segment: form.segment };
    if (form.scheduled_at) payload.scheduled_at = new Date(form.scheduled_at).toISOString();
    return payload;
  };

  const previewClick = async () => {
    setBusy(true); setError(''); setResult(null);
    try { setPreview(await previewBroadcast(buildPayload())); }
    catch (err) { setError(err.response?.data?.message || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const sendClick = async () => {
    if (!preview?.confirm_token) return;
    if (!(await confirm({
      title: 'Send broadcast now?',
      message: `This will email ${preview.expected_recipients.toLocaleString()} recipient(s) immediately. You can't recall it once sent.`,
      confirmLabel: 'Send now',
    }))) return;
    setBusy(true); setError('');
    try {
      const data = await sendBroadcast({ ...buildPayload(), confirm_token: preview.confirm_token });
      setResult({ kind: 'sent', message: data.message });
      setPreview(null);
      resetForm();
      refreshList();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Send failed');
    } finally { setBusy(false); }
  };

  const saveDraftClick = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const data = editingId
        ? await updateBroadcast(editingId, buildPayload())
        : await saveBroadcastDraft(buildPayload());
      setResult({ kind: 'draft', message: data.message || (form.scheduled_at ? 'Scheduled.' : 'Draft saved.') });
      resetForm();
      refreshList();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Save failed');
    } finally { setBusy(false); }
  };

  const sendNowFromList = async (campaign) => {
    if (!(await confirm({
      title: 'Send broadcast now?',
      message: `“${campaign.subject}” will be sent immediately to its saved audience.`,
      confirmLabel: 'Send now',
    }))) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const data = await runBroadcastNow(campaign.id);
      setResult({ kind: 'sent', message: data.message });
      refreshList();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Send failed');
    } finally { setBusy(false); }
  };

  const cancelFromList = async (campaign) => {
    if (!(await confirm({
      title: 'Cancel scheduled broadcast?',
      message: `“${campaign.subject}” will be removed from the schedule. You can re-schedule it later.`,
      confirmLabel: 'Cancel send',
    }))) return;
    try { await cancelBroadcast(campaign.id); refreshList(); }
    catch (err) { setError(err.response?.data?.error || 'Cancel failed'); }
  };

  const deleteFromList = async (campaign) => {
    if (!(await confirm({
      title: 'Delete broadcast?',
      message: `“${campaign.subject}” will be permanently removed.`,
      danger: true,
      confirmLabel: 'Delete',
    }))) return;
    try { await deleteBroadcast(campaign.id); refreshList(); if (editingId === campaign.id) resetForm(); }
    catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const visibleRows = statusFilter === 'all' ? rows : rows.filter(r => r.status === statusFilter);
  const minDatetimeLocal = new Date(Date.now() + 5 * 60_000).toISOString().slice(0, 16);

  return (
    <>
      <PageHeader title="Broadcasts" subtitle="Mass-email a user segment. Send now, save as draft, or schedule for later." />

      {result && (
        <div style={{ background: '#DCFCE7', color: '#166534', padding: '14px 18px', borderRadius: 12, marginBottom: 18, fontWeight: 600 }}>
          ✓ {result.message}
        </div>
      )}
      {error && <div className="alert alert-danger" style={{ marginBottom: 18 }}>{error}</div>}

      {/* ── Compose / edit ── */}
      <div className="ds-panel" style={{ marginBottom: 24 }}>
        <div className="ds-panel-body">
          {editingId && (
            <div style={{ background: '#FEF3C7', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#92400E', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span><strong>Editing campaign #{editingId}</strong> — save to overwrite, or cancel to start fresh.</span>
              <button className="btn btn-secondary btn-sm" onClick={resetForm}>Cancel edit</button>
            </div>
          )}

          <div className="form-group"><label className="form-label">Subject</label><input className="form-input" value={form.subject} onChange={(e) => { setForm(p => ({ ...p, subject: e.target.value })); setPreview(null); }} placeholder="This week's hand-picked deals" /></div>
          <div className="form-group"><label className="form-label">Body</label><textarea className="form-input" rows={6} value={form.body} onChange={(e) => { setForm(p => ({ ...p, body: e.target.value })); setPreview(null); }} placeholder="Plain text. Personalize with the recipient's name in the future via {{name}}." /></div>

          <div className="form-group">
            <label className="form-label">Audience</label>
            <select className="form-input form-select" value={form.segment.audience} onChange={(e) => { setForm(p => ({ ...p, segment: { audience: e.target.value } })); setPreview(null); }}>
              <option value="all">All users (opt-in only — opt-outs always skipped)</option>
              <option value="opted_in">Opted in (same as all — explicit)</option>
              <option value="verified">Email verified only</option>
              <option value="tier">Tier…</option>
              <option value="role">Primary role…</option>
            </select>
          </div>

          {form.segment.audience === 'tier' && (
            <div className="form-group"><label className="form-label">Tier</label><select className="form-input form-select" value={form.segment.tier || ''} onChange={(e) => { setForm(p => ({ ...p, segment: { ...p.segment, tier: e.target.value } })); setPreview(null); }}><option value="">Pick a tier…</option><option value="silver">Silver</option><option value="gold">Gold</option><option value="platinum">Platinum</option></select></div>
          )}
          {form.segment.audience === 'role' && (
            <div className="form-group"><label className="form-label">Primary role (users.role)</label><input className="form-input" value={form.segment.role || ''} onChange={(e) => { setForm(p => ({ ...p, segment: { ...p.segment, role: e.target.value } })); setPreview(null); }} placeholder="user, merchant_admin, partner_admin" /></div>
          )}

          <div className="form-group">
            <label className="form-label">Schedule for later (optional)</label>
            <input
              type="datetime-local"
              className="form-input"
              min={minDatetimeLocal}
              value={form.scheduled_at}
              onChange={(e) => { setForm(p => ({ ...p, scheduled_at: e.target.value })); setPreview(null); }}
            />
            <p style={{ fontSize: 12, color: '#5A6378', marginTop: 6 }}>
              Leave blank to send immediately (via the confirm flow) or save as a draft.
              When set, "Save" stores the campaign as <strong>scheduled</strong>; the
              scheduler picks it up within 5 minutes of that time.
            </p>
          </div>

          {preview ? (
            <div style={{ background: '#FEF3C7', borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#92400E' }}>Will send to {preview.expected_recipients.toLocaleString()} recipient(s).</p>
              {preview.sample_emails?.length > 0 && (
                <p style={{ fontSize: 12, color: '#5A6378', marginTop: 6 }}>Sample: {preview.sample_emails.join(' · ')}</p>
              )}
              <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 6 }}>Confirm token expires in {Math.round(preview.expires_in_seconds / 60)} minute(s).</p>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={saveDraftClick} disabled={busy || !form.subject || !form.body}>
              {busy ? 'Saving…' : (form.scheduled_at ? <><i className="bi bi-calendar-event" /> Schedule</> : <><i className="bi bi-save" /> Save as draft</>)}
            </button>
            {!preview ? (
              <button className="btn btn-secondary" onClick={previewClick} disabled={busy || !form.subject || !form.body || !!form.scheduled_at}>
                {busy ? 'Counting…' : 'Preview & send now'}
              </button>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={() => setPreview(null)}>Edit</button>
                <button className="btn btn-primary" onClick={sendClick} disabled={busy}>{busy ? 'Sending…' : `Confirm — send ${preview.expected_recipients}`}</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Campaign list ── */}
      <div className="ds-panel">
        <div className="ds-panel-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Campaign history</h3>
            <select className="form-input form-select" style={{ maxWidth: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="draft">Drafts</option>
              <option value="scheduled">Scheduled</option>
              <option value="sent">Sent</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {listLoading ? (
            <p style={{ color: '#8B94A8', textAlign: 'center', padding: '20px 0' }}>Loading…</p>
          ) : visibleRows.length === 0 ? (
            <EmptyState message={statusFilter === 'all' ? 'No campaigns yet.' : `No ${statusFilter} campaigns.`} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visibleRows.map(c => {
                const meta = CAMPAIGN_STATUS_META[c.status] || { label: c.status, bg: '#F1F5F9', fg: '#475569' };
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: '#F7F8FC', borderRadius: 10, gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ background: meta.bg, color: meta.fg, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{meta.label}</span>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{c.subject}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#5A6378' }}>
                        Audience: {c.segment_filter?.audience || 'all'}
                        {c.segment_filter?.tier ? ` · ${c.segment_filter.tier}` : ''}
                        {c.segment_filter?.role ? ` · ${c.segment_filter.role}` : ''}
                        {c.scheduled_at && c.status === 'scheduled' && <> · scheduled {new Date(c.scheduled_at).toLocaleString()}</>}
                        {c.status === 'sent' && <> · {c.sent_count || 0} delivered</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(c.status === 'draft' || c.status === 'scheduled') && (
                        <button className="btn btn-secondary btn-sm" onClick={() => loadIntoForm(c)}>Edit</button>
                      )}
                      {(c.status === 'draft' || c.status === 'scheduled') && (
                        <button className="btn btn-primary btn-sm" onClick={() => sendNowFromList(c)} disabled={busy}>Send now</button>
                      )}
                      {c.status === 'scheduled' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => cancelFromList(c)}>Cancel</button>
                      )}
                      {(c.status === 'draft' || c.status === 'cancelled') && (
                        <button className="btn btn-secondary btn-sm" onClick={() => deleteFromList(c)} style={{ color: '#DC2626' }}>Delete</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {confirmNode}
    </>
  );
}

/* ============================= Settings ============================= */

function SettingsTab() {
  const { rows, loading, bulkUpdate } = useSettings();
  const [edits, setEdits]     = useState({});
  const [saving, setSaving]   = useState(false);
  const [result, setResult]   = useState(null);

  const grouped = useMemoBy(rows, 'group');

  const setVal = (key, value) => setEdits(p => ({ ...p, [key]: value }));

  const save = async () => {
    const updates = Object.entries(edits).map(([key, value]) => ({ key, value: String(value) }));
    if (updates.length === 0) return;
    setSaving(true); setResult(null);
    try {
      const r = await bulkUpdate(updates);
      setEdits({});
      setResult(r);
    } catch (err) {
      setResult({ error: err.response?.data?.message || 'Save failed' });
    } finally { setSaving(false); }
  };

  // Maintenance mode quick toggle.
  const maintenance = rows.find(r => r.key === 'maintenance.enabled');
  const maintenanceEnabled = (edits['maintenance.enabled'] !== undefined)
    ? !!edits['maintenance.enabled']
    : !!maintenance?.typed_value;

  if (loading) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Loyalty thresholds, cashback timing, referral bonuses, maintenance mode"
        action={Object.keys(edits).length > 0 && (
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : `Save ${Object.keys(edits).length} change(s)`}</button>
        )}
      />

      {result?.error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{result.error}</div>}
      {result?.updated && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          ✓ Updated {result.updated.length} setting(s){Object.keys(result.errors || {}).length > 0 && ` · ${Object.keys(result.errors).length} rejected`}
        </div>
      )}

      {/* Maintenance toggle — surfaced prominently for emergency use */}
      {maintenance && (
        <div className="ds-card" style={{ marginBottom: 18, background: maintenanceEnabled ? '#FEE2E2' : '#FAFBFD', border: `1px solid ${maintenanceEnabled ? '#FCA5A5' : '#E4E7F0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: maintenanceEnabled ? '#991B1B' : '#0F1225' }}>
                {maintenanceEnabled ? '⚠ Maintenance mode is ON — non-admin requests get 503' : 'Site is open to all users'}
              </p>
              <p style={{ fontSize: 13, color: '#5A6378', marginTop: 4 }}>{maintenance.description}</p>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={maintenanceEnabled} onChange={(e) => setVal('maintenance.enabled', e.target.checked ? '1' : '0')} />
              <span style={{ fontWeight: 600 }}>{maintenanceEnabled ? 'On' : 'Off'}</span>
            </label>
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} className="ds-panel" style={{ marginBottom: 18 }}>
          <div className="ds-panel-body">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, textTransform: 'capitalize' }}>{group}</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              {items.map(s => {
                if (s.key === 'maintenance.enabled') return null; // shown above
                const current = edits[s.key] !== undefined ? edits[s.key] : s.value;
                const changed = edits[s.key] !== undefined && edits[s.key] !== s.value;
                return (
                  <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: '1px solid #F0F2F8' }}>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 14 }}>{s.label || s.key}</p>
                      <p style={{ fontSize: 11.5, fontFamily: 'ui-monospace, monospace', color: '#8B94A8', marginTop: 2 }}>{s.key} · <span style={{ color: '#5A6378' }}>{s.type}</span></p>
                      {s.description && <p style={{ fontSize: 12, color: '#5A6378', marginTop: 4 }}>{s.description}</p>}
                    </div>
                    <input
                      className="form-input"
                      style={{ width: 200, fontFamily: s.type === 'json' ? 'ui-monospace, monospace' : 'inherit', borderColor: changed ? '#6B2FD9' : undefined }}
                      type={['int','float'].includes(s.type) ? 'number' : 'text'}
                      step={s.type === 'float' ? '0.01' : undefined}
                      value={current ?? ''}
                      onChange={(e) => setVal(s.key, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Integration tests */}
      <div className="ds-panel" style={{ marginBottom: 18 }}>
        <div className="ds-panel-body">
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Integration tests</h3>
          <p style={{ fontSize: 13, color: '#5A6378', marginBottom: 14 }}>Synthetic round-trips so you can confirm wiring without leaving the admin.</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <IntegrationTestButton name="stripe" label={<><i className="bi bi-lightning-charge-fill" /> Test Stripe</>} />
            <IntegrationTestButton name="mail"   label={<><i className="bi bi-envelope" /> Test Mail</>} />
          </div>
        </div>
      </div>
    </>
  );
}

function IntegrationTestButton({ name, label }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try { setResult(await testIntegration(name)); }
    catch (err) { setResult({ success: false, detail: err.response?.data?.detail || err.message }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button className="btn btn-secondary" disabled={busy} onClick={run}>{busy ? '…' : label}</button>
      {result && (
        <span style={{ fontSize: 12, color: result.success ? '#166534' : '#991B1B', maxWidth: 280 }}>
          {result.success ? `✓ ${result.detail || 'OK'} (${result.duration_ms}ms)` : `✗ ${result.detail || 'Failed'}`}
        </span>
      )}
    </div>
  );
}

// Small helper that groups an array by a string key (used by SettingsTab).
function useMemoBy(arr, key) {
  return arr.reduce((acc, item) => {
    const k = item[key] || 'other';
    (acc[k] ||= []).push(item);
    return acc;
  }, {});
}

/* ============================= Revenue ============================= */

const REVENUE_PERIODS = [
  { id: '7d',   label: '7 days' },
  { id: '30d',  label: '30 days' },
  { id: '90d',  label: '90 days' },
  { id: '12mo', label: '12 months' },
];

function RevenueTab() {
  const [period, setPeriod] = useState('30d');
  const [tenant, setTenant] = useState('');
  const { clients } = useAppClients();
  const { data, loading } = useRevenue(period, tenant);

  if (loading) return <LoadingPage />;
  if (!data) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-cash" />} title="Revenue endpoint unavailable" /></div></div>;

  const t = data.totals || {};
  const trendMax = Math.max(...(data.trend || []).map(d => d.commission), 1);

  return (
    <>
      <PageHeader
        title="Revenue"
        subtitle={`${data.period_label} · ${t.redemptions?.toLocaleString() ?? 0} redemptions`}
      />

      {/* Filter bar — .ds-card is flex-direction:column, so force row here or
          the Period buttons + Tenant select stack vertically (Perkvora bug 27-07 #2). */}
      <div className="ds-card" style={{ padding: 12, marginBottom: 18, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#5A6378', fontWeight: 600, marginRight: 4 }}>Period</span>
        {REVENUE_PERIODS.map(p => (
          <button key={p.id} className="btn btn-sm" onClick={() => setPeriod(p.id)}
            style={{ background: period === p.id ? '#6B2FD9' : '#fff', color: period === p.id ? '#fff' : '#5A6378', border: '1px solid ' + (period === p.id ? '#6B2FD9' : '#E4E7F0'), padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
            {p.label}
          </button>
        ))}
        <div style={{ width: 1, height: 22, background: '#E4E7F0', margin: '0 6px' }} />
        <span style={{ fontSize: 12, color: '#5A6378', fontWeight: 600, marginRight: 4 }}>Tenant</span>
        <select className="form-input form-select" value={tenant} onChange={e => setTenant(e.target.value)} style={{ width: 180, fontSize: 13 }}>
          <option value="">All tenants</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.app_id}</option>)}
        </select>
      </div>

      {/* Headline cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard label="Gross commission" value={`$${(t.gross_commission ?? 0).toLocaleString()}`} icon={<i className="bi bi-cash-stack" />} />
        <StatCard label="Cashback paid"   value={`$${(t.cashback_paid ?? 0).toLocaleString()}`} icon={<i className="bi bi-cash-coin" />} />
        <StatCard label={`Net margin (${t.net_margin >= 0 ? 'positive' : 'negative'})`} value={`$${(t.net_margin ?? 0).toLocaleString()}`} icon={t.net_margin >= 0 ? <i className="bi bi-graph-up" /> : <i className="bi bi-graph-down" />} />
        <StatCard label="Effective take rate" value={`${(t.effective_take_rate_pct ?? 0).toFixed(2)}%`} icon={<i className="bi bi-calculator" />} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard label="Cashback approved (pending payout)" value={`$${(t.cashback_approved ?? 0).toLocaleString()}`} icon={<i className="bi bi-hourglass-split" />} />
        <StatCard label="Cashback pending (lock period)" value={`$${(t.cashback_pending ?? 0).toLocaleString()}`} icon={<i className="bi bi-lock" />} />
        <StatCard label="Gross order value driven" value={`$${(t.total_order_value ?? 0).toLocaleString()}`} icon={<i className="bi bi-cart" />} />
      </div>

      {/* Trend mini chart */}
      {data.trend?.length > 0 && (
        <div className="ds-card" style={{ padding: 18, marginBottom: 18 }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Daily commission trend</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 100 }}>
            {data.trend.map((d, i) => (
              <div key={i} title={`${d.date}: $${d.commission.toLocaleString()} (${d.redemptions} redemptions)`}
                style={{ flex: 1, minWidth: 4, background: 'linear-gradient(180deg, #6B2FD9, #D946EF)', height: `${(d.commission / trendMax) * 100}%`, borderRadius: '3px 3px 0 0', minHeight: 2 }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8B94A8', marginTop: 6 }}>
            <span>{data.trend[0]?.date}</span>
            <span>${(trendMax).toLocaleString()} peak</span>
            <span>{data.trend[data.trend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Per-tenant */}
      <div className="ds-card" style={{ padding: 0, marginBottom: 18, overflow: 'hidden' }}>
        <p style={{ fontSize: 13, fontWeight: 700, padding: '14px 18px', borderBottom: '1px solid #F0F2F8' }}>By tenant</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr style={{ background: '#FAFBFD' }}>
                <th style={{ padding: '10px 18px', textAlign: 'left', fontSize: 11, color: '#5A6378', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tenant</th>
                <th style={{ padding: '10px 18px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Commission</th>
                <th style={{ padding: '10px 18px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cashback</th>
                <th style={{ padding: '10px 18px', textAlign: 'right', fontSize: 11, color: '#5A6378', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Net margin</th>
              </tr>
            </thead>
            <tbody>
              {data.by_tenant?.map(r => (
                <tr key={r.app_client_id} style={{ borderTop: '1px solid #F0F2F8' }}>
                  <td style={{ padding: '12px 18px', fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{r.app_name}</div>
                    <div style={{ fontSize: 11, color: '#8B94A8', fontFamily: 'ui-monospace, monospace' }}>{r.app_id}</div>
                  </td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>${r.commission.toLocaleString()}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 13, color: '#5A6378', fontVariantNumeric: 'tabular-nums' }}>${r.cashback.toLocaleString()}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: r.net_margin >= 0 ? '#166534' : '#991B1B' }}>${r.net_margin.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top merchants + recent payouts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
        <div className="ds-card" style={{ padding: 18 }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Top merchants by commission</p>
          {data.top_merchants?.length === 0 ? <p style={{ fontSize: 12, color: '#8B94A8' }}>No redemptions in this period.</p> : (
            <div>
              {data.top_merchants?.map((m, i) => (
                <div key={m.merchant_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #F0F2F8' }}>
                  <span style={{ fontSize: 12, color: '#8B94A8', fontWeight: 700, width: 22, textAlign: 'right' }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{m.name}</span>
                  <span style={{ fontSize: 12, color: '#8B94A8' }}>{m.redemptions} ×</span>
                  <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>${m.commission.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ds-card" style={{ padding: 18 }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Recent payouts</p>
          {data.recent_payouts?.length === 0 ? <p style={{ fontSize: 12, color: '#8B94A8' }}>No paid payouts yet.</p> : (
            <div>
              {data.recent_payouts?.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #F0F2F8' }}>
                  <span style={{ flex: 1, fontSize: 12, color: '#8B94A8' }}>user #{p.user_id}</span>
                  <span style={{ fontSize: 12, color: '#5A6378' }}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>${Number(p.amount).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ============================= Tier Lifecycle ============================= */

const TIER_PALETTE = {
  silver:   { bg: '#F0F2F8', fg: '#5A6378', label: 'Silver',  icon: <i className="bi bi-award" /> },
  gold:     { bg: '#FEF3C7', fg: '#92400E', label: 'Gold',    icon: <i className="bi bi-award-fill" /> },
  platinum: { bg: '#EBE0FF', fg: '#6B2FD9', label: 'Platinum', icon: <i className="bi bi-gem" /> },
  unranked: { bg: '#FEE2E2', fg: '#991B1B', label: 'Unranked', icon: '○' },
};

function TierLifecycleTab() {
  const [nudgeBand, setNudgeBand] = useState(10);
  const { data, loading } = useTierLifecycle(nudgeBand);

  if (loading) return <LoadingPage />;
  if (!data) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-trophy" />} title="Tier endpoint unavailable" /></div></div>;

  const dist = data.distribution || {};
  const totalDist = Object.values(dist).reduce((a, b) => a + b, 0);

  return (
    <>
      <PageHeader
        title="Tier Lifecycle"
        subtitle={`Thresholds: Silver ≥ $${data.thresholds?.silver}  ·  Gold ≥ $${data.thresholds?.gold}  ·  Platinum ≥ $${data.thresholds?.platinum} (rolling 12mo)`}
      />

      {/* Tier distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        {['platinum', 'gold', 'silver', 'unranked'].map(k => {
          const meta = TIER_PALETTE[k];
          const count = dist[k] ?? 0;
          const pct = totalDist > 0 ? Math.round((count / totalDist) * 100) : 0;
          return (
            <div key={k} className="ds-card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 26 }}>{meta.icon}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{meta.label}</p>
                  <p style={{ fontSize: 22, fontWeight: 800, color: meta.fg, fontVariantNumeric: 'tabular-nums' }}>{count.toLocaleString()}</p>
                </div>
                <span style={{ padding: '2px 8px', background: meta.bg, color: meta.fg, borderRadius: 6, fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Nudge-band input */}
      <div className="ds-card" style={{ padding: 14, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: '#5A6378', fontWeight: 600 }}>Nudge band</span>
        <input type="number" className="form-input" style={{ width: 80, fontSize: 13 }} min={1} max={500} value={nudgeBand} onChange={e => setNudgeBand(Number(e.target.value) || 10)} />
        <span style={{ fontSize: 12, color: '#8B94A8' }}>dollars from next tier counts as "about to tier up"</span>
      </div>

      {/* Three lists */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 18 }}>
        <TierList title={<><i className="bi bi-stars" /> Just tiered up (30d)</>} rows={data.just_tiered_up} emptyText="No tier-up events in the last 30 days."
          renderRow={(r) => (<>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{r.user_name || r.user_email}</span>
            <span style={{ padding: '2px 7px', borderRadius: 5, background: TIER_PALETTE[r.tier_name]?.bg ?? '#F0F2F8', color: TIER_PALETTE[r.tier_name]?.fg ?? '#5A6378', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{r.tier_name || '—'}</span>
            <span style={{ fontSize: 11, color: '#8B94A8' }}>{r.unlocked_at ? new Date(r.unlocked_at).toLocaleDateString() : '—'}</span>
          </>)}
        />
        <TierList title={<><i className="bi bi-lightning-charge-fill" /> About to tier up (within ${nudgeBand})</>} rows={data.about_to_tier_up} emptyText="No users currently within the nudge band."
          renderRow={(r) => (<>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{r.user_name || r.user_email}</span>
            <span style={{ fontSize: 11, color: '#8B94A8' }}>earned ${r.earned_12mo}</span>
            <span style={{ padding: '2px 7px', borderRadius: 5, background: '#DCFCE7', color: '#166534', fontSize: 11, fontWeight: 700 }}>${r.gap_to_next} to {r.next_tier}</span>
          </>)}
        />
        <TierList title={<><i className="bi bi-exclamation-triangle" /> At risk of tier-down</>} rows={data.at_risk} emptyText="Nobody is at risk right now."
          renderRow={(r) => (<>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{r.user_name || r.user_email}</span>
            <span style={{ padding: '2px 7px', borderRadius: 5, background: TIER_PALETTE[r.persisted_tier]?.bg ?? '#F0F2F8', color: TIER_PALETTE[r.persisted_tier]?.fg ?? '#5A6378', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{r.persisted_tier}</span>
            <span style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>−${r.shortfall} short</span>
          </>)}
        />
      </div>
    </>
  );
}

function TierList({ title, rows, emptyText, renderRow }) {
  return (
    <div className="ds-card" style={{ padding: 16 }}>
      <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{title}</p>
      {!rows || rows.length === 0 ? (
        <p style={{ fontSize: 12, color: '#8B94A8' }}>{emptyText}</p>
      ) : (
        <div>
          {rows.map((r, i) => (
            <div key={r.user_id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #F0F2F8' }}>
              {renderRow(r)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================= Banners ============================= */

function BannersTab() {
  const { banners, loading, refresh } = useBanners();
  const { clients } = useAppClients();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const remove = async (b) => {
    if (!(await confirm({ title: 'Delete banner?', message: `“${b.title}” will be removed from the rotation immediately.`, danger: true, confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/admin/banners/${b.id}`); refresh(); toast.success('Banner deleted'); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <LoadingPage />;

  return (
    <>
      <PageHeader
        title="Banners"
        subtitle="Promo strips for the homepage. Schedule with start/end dates, prioritise with lower numbers, and watch CTR per banner."
        action={<button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Banner</button>}
      />

      {banners.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-bullseye" />} title="No banners yet" subtitle="Create the first promo banner to surface a deal on the homepage." /></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {banners.map(b => (
            <div key={b.id} className="ds-card">
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                {b.image_url && <img src={b.image_url} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700 }}>{b.title}</span>
                    <span className={`badge ${b.is_live ? 'badge-green' : 'badge-red'}`}>{b.is_live ? '● Live' : '● Off'}</span>
                    <span style={{ fontSize: 11, color: '#8B94A8' }}>priority {b.priority} · {b.placement}</span>
                  </div>
                  {b.subtitle && <p style={{ fontSize: 13, color: '#5A6378' }}>{b.subtitle}</p>}
                  <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 12, color: '#5A6378' }}>
                    <span><i className="bi bi-eye" /> {b.impression_count?.toLocaleString() || 0} impressions</span>
                    <span><i className="bi bi-cursor" /> {b.click_count?.toLocaleString() || 0} clicks</span>
                    <span style={{ fontWeight: 700, color: b.ctr_pct > 1 ? '#166534' : '#5A6378' }}>{b.ctr_pct}% CTR</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(b)}>Edit</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => remove(b)} style={{ color: '#991B1B' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showNew || editing) && (
        <BannerFormModal
          banner={editing}
          clients={clients}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { refresh(); setShowNew(false); setEditing(null); }}
        />
      )}
      {confirmNode}
    </>
  );
}

function BannerFormModal({ banner, clients, onClose, onSaved }) {
  const [form, setForm] = useState({
    app_client_id: banner?.app_client_id || '',
    title:    banner?.title || '',
    subtitle: banner?.subtitle || '',
    image_url:banner?.image_url || '',
    cta_text: banner?.cta_text || 'Shop now',
    cta_url:  banner?.cta_url || '',
    placement:banner?.placement || 'homepage_top',
    is_active:banner?.is_active ?? true,
    priority: banner?.priority ?? 100,
    starts_at:banner?.starts_at ? banner.starts_at.slice(0, 16) : '',
    ends_at:  banner?.ends_at   ? banner.ends_at.slice(0, 16)   : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    const payload = { ...form };
    if (!payload.app_client_id) payload.app_client_id = null;
    if (!payload.starts_at) payload.starts_at = null;
    if (!payload.ends_at)   payload.ends_at = null;
    try {
      if (banner) await api.patch(`/admin/banners/${banner.id}`, payload);
      else        await api.post('/admin/banners', payload);
      onSaved();
    } catch (err) {
      const fe = err.response?.data?.errors;
      setError(fe ? Object.values(fe).flat()[0] : (err.response?.data?.message || 'Save failed'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{banner ? 'Edit Banner' : 'New Banner'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={submit}>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="form-group"><label className="form-label">Title</label><input className="form-input" required value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Subtitle</label><input className="form-input" value={form.subtitle} onChange={(e) => setForm(p => ({ ...p, subtitle: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Image URL</label><input className="form-input" type="url" value={form.image_url} onChange={(e) => setForm(p => ({ ...p, image_url: e.target.value }))} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label className="form-label">CTA text</label><input className="form-input" value={form.cta_text} onChange={(e) => setForm(p => ({ ...p, cta_text: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">CTA URL</label><input className="form-input" type="url" value={form.cta_url} onChange={(e) => setForm(p => ({ ...p, cta_url: e.target.value }))} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group"><label className="form-label">Tenant</label><select className="form-input form-select" value={form.app_client_id || ''} onChange={(e) => setForm(p => ({ ...p, app_client_id: e.target.value }))}><option value="">All tenants</option>{clients.map(c => <option key={c.id} value={c.id}>{c.app_id}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Placement</label><input className="form-input" value={form.placement} onChange={(e) => setForm(p => ({ ...p, placement: e.target.value }))} style={{ fontFamily: 'ui-monospace, monospace' }} /></div>
              <div className="form-group"><label className="form-label">Priority</label><input className="form-input" type="number" min="0" max="1000" value={form.priority} onChange={(e) => setForm(p => ({ ...p, priority: Number(e.target.value) }))} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label className="form-label">Starts at</label><input className="form-input" type="datetime-local" value={form.starts_at || ''} onChange={(e) => setForm(p => ({ ...p, starts_at: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Ends at</label><input className="form-input" type="datetime-local" value={form.ends_at || ''} onChange={(e) => setForm(p => ({ ...p, ends_at: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.is_active} onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))} /><span style={{ fontWeight: 600 }}>Active</span></label></div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (banner ? 'Save' : 'Create')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================= Blog ============================= */

function BlogTab() {
  const { posts, meta, categories, loading, filters, setFilters, refresh } = useBlog();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [showPost, setShowPost] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);

  const removePost = async (p) => {
    if (!(await confirm({ title: 'Delete post?', message: `“${p.title}” will be removed and stop appearing on /blog. This can't be undone.`, danger: true, confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/admin/blog/posts/${p.id}`); refresh(); toast.success('Post deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <>
      <PageHeader
        title="Blog"
        subtitle={`${meta.total ?? 0} posts · ${categories.length} categories`}
        action={(
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setShowCatModal(true)}>Categories</button>
            <button className="btn btn-primary" onClick={() => setShowNewPost(true)}>+ New Post</button>
          </div>
        )}
      />

      <div className="ds-panel" style={{ marginBottom: 16 }}>
        <div className="ds-panel-body" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="form-input form-select" style={{ width: 160 }} value={filters.status} onChange={(e) => setFilters(p => ({ ...p, status: e.target.value, page: 1 }))}>
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <input className="form-input" placeholder="Search title or excerpt…" value={filters.q} onChange={(e) => setFilters(p => ({ ...p, q: e.target.value, page: 1 }))} style={{ flex: 1 }} />
        </div>
      </div>

      {loading ? <LoadingPage /> : posts.length === 0 ? (
        <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-pencil-square" />} title="No posts" subtitle="Write your first blog post to start publishing." /></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {posts.map(p => (
            <div key={p.id} className="ds-card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              {p.hero_image_url && <img src={p.hero_image_url} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span className={`badge ${p.status === 'published' ? 'badge-green' : p.status === 'draft' ? 'badge' : 'badge-red'}`}>{p.status}</span>
                  {p.category && <span style={{ fontSize: 11, color: '#6B2FD9', fontWeight: 600 }}>{p.category.name}</span>}
                  <span style={{ fontSize: 11, color: '#8B94A8' }}><i className="bi bi-eye" /> {p.view_count} · /{p.slug}</span>
                </div>
                <p style={{ fontWeight: 700 }}>{p.title}</p>
                {p.excerpt && <p style={{ fontSize: 13, color: '#5A6378', marginTop: 4 }}>{p.excerpt}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPost(p)}>Edit</button>
                <button className="btn btn-secondary btn-sm" onClick={() => removePost(p)} style={{ color: '#991B1B' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {meta.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={filters.page === 1} onClick={() => setFilters(p => ({ ...p, page: Math.max(1, p.page - 1) }))}>← Prev</button>
          <span style={{ padding: '8px 14px', fontSize: 13, color: '#5A6378' }}>Page {meta.current_page} of {meta.last_page}</span>
          <button className="btn btn-secondary btn-sm" disabled={filters.page === meta.last_page} onClick={() => setFilters(p => ({ ...p, page: Math.min(meta.last_page, p.page + 1) }))}>Next →</button>
        </div>
      )}

      {(showPost || showNewPost) && (
        <BlogPostModal post={showPost} categories={categories} onClose={() => { setShowPost(null); setShowNewPost(false); }} onSaved={() => { refresh(); setShowPost(null); setShowNewPost(false); }} />
      )}
      {showCatModal && <BlogCategoryModal categories={categories} onClose={() => setShowCatModal(false)} onSaved={refresh} />}
      {confirmNode}
    </>
  );
}

function BlogPostModal({ post, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: post?.title || '',
    category_id: post?.category_id || '',
    excerpt: post?.excerpt || '',
    body: post?.body || '',
    hero_image_url: post?.hero_image_url || '',
    seo_title: post?.seo_title || '',
    seo_description: post?.seo_description || '',
    status: post?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    const payload = { ...form };
    if (!payload.category_id) payload.category_id = null;
    try {
      if (post) await api.patch(`/admin/blog/posts/${post.id}`, payload);
      else      await api.post('/admin/blog/posts', payload);
      onSaved();
    } catch (err) {
      const fe = err.response?.data?.errors;
      setError(fe ? Object.values(fe).flat()[0] : (err.response?.data?.message || 'Save failed'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{post ? `Edit · ${post.title}` : 'New Post'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={submit}>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="form-group"><label className="form-label">Title</label><input className="form-input" required value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label className="form-label">Category</label><select className="form-input form-select" value={form.category_id || ''} onChange={(e) => setForm(p => ({ ...p, category_id: e.target.value }))}><option value="">— None —</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Status</label><select className="form-input form-select" value={form.status} onChange={(e) => setForm(p => ({ ...p, status: e.target.value }))}><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></div>
            </div>
            <div className="form-group"><label className="form-label">Excerpt (one-liner)</label><input className="form-input" value={form.excerpt} onChange={(e) => setForm(p => ({ ...p, excerpt: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Body (Markdown)</label><textarea className="form-input" rows={10} value={form.body} onChange={(e) => setForm(p => ({ ...p, body: e.target.value }))} style={{ fontFamily: 'ui-monospace, monospace' }} /></div>
            <div className="form-group"><label className="form-label">Hero image URL</label><input className="form-input" type="url" value={form.hero_image_url} onChange={(e) => setForm(p => ({ ...p, hero_image_url: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">SEO title</label><input className="form-input" value={form.seo_title} onChange={(e) => setForm(p => ({ ...p, seo_title: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">SEO description</label><input className="form-input" value={form.seo_description} onChange={(e) => setForm(p => ({ ...p, seo_description: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (post ? 'Save' : 'Create')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function BlogCategoryModal({ categories, onClose, onSaved }) {
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post('/admin/blog/categories', { name, description: desc });
      setName(''); setDesc('');
      onSaved();
    } catch (err) { toast.error('Save failed', err.response?.data?.message); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!(await confirm({ title: 'Delete category?', message: `“${c.name}” will be removed. Posts in it will become uncategorized.`, danger: true, confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/admin/blog/categories/${c.id}`); onSaved(); toast.success('Category deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Blog categories</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {categories.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#FAFBFD', borderRadius: 8 }}>
                <span style={{ flex: 1, fontWeight: 600 }}>{c.name}</span>
                <span style={{ fontSize: 12, color: '#8B94A8' }}>{c.posts_count || 0} posts</span>
                <button className="btn btn-secondary btn-sm" onClick={() => remove(c)} style={{ color: '#991B1B' }}>Delete</button>
              </div>
            ))}
            {categories.length === 0 && <p style={{ fontSize: 13, color: '#8B94A8' }}>No categories yet.</p>}
          </div>
          <div style={{ borderTop: '1px solid #F0F2F8', paddingTop: 16 }}>
            <p style={{ fontWeight: 700, marginBottom: 10 }}>Add category</p>
            <div className="form-group"><input className="form-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="form-group"><input className="form-input" placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
            <button className="btn btn-primary btn-full" disabled={busy || !name.trim()} onClick={add}>{busy ? 'Saving…' : 'Add'}</button>
          </div>
        </div>
      </div>
      {confirmNode}
    </div>
  );
}

/* ============================= Payment Settings ============================= */

const PAYMENT_MODE_META = {
  live:           { label: '● LIVE',          bg: '#FEE2E2', fg: '#991B1B' },
  test:           { label: '● Test mode',     bg: '#FEF3C7', fg: '#92400E' },
  not_configured: { label: '○ Not configured',bg: '#F0F2F8', fg: '#5A6378' },
};

const SOURCE_META = {
  db:    { label: 'DB override',   bg: '#EBE0FF', fg: '#6B2FD9' },
  env:   { label: 'from .env',     bg: '#DBEAFE', fg: '#1E40AF' },
  unset: { label: 'not set',       bg: '#FEE2E2', fg: '#991B1B' },
};

function PaymentSettingsTab() {
  const { data, loading, refresh } = usePaymentSettings();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [edits, setEdits] = useState({}); // { field: value } for in-flight edits
  const [shown, setShown] = useState({}); // { field: bool } show/hide newly typed value
  const [busy, setBusy]   = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');

  if (loading) return <LoadingPage />;
  if (!data) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-credit-card" />} title="Payment settings endpoint unavailable" /></div></div>;

  const mode = PAYMENT_MODE_META[data.mode] || PAYMENT_MODE_META.not_configured;
  const dirtyFields = Object.keys(edits).filter((f) => edits[f]?.trim());

  const save = async () => {
    if (dirtyFields.length === 0) return;
    setBusy(true); setSavedMsg('');
    try {
      const updates = dirtyFields.map((field) => ({ field, value: edits[field].trim() }));
      const res = await updatePaymentSettings(updates);
      if (res.errors && Object.keys(res.errors).length > 0) {
        const lines = Object.entries(res.errors).map(([k, v]) => `${k}: ${v}`).join(' · ');
        toast.error('Some fields failed', lines);
      }
      setEdits({}); setShown({});
      setSavedMsg(`✓ ${res.applied?.length || 0} field(s) saved.`);
      await refresh();
    } catch (err) {
      toast.error('Save failed', err.response?.data?.message);
    } finally {
      setBusy(false);
    }
  };

  const revert = async (field) => {
    if (!(await confirm({
      title: 'Clear database override?',
      message: `${field} will fall back to whatever .env has set (if anything). Restart the queue worker so the change takes effect on the next mail send.`,
      confirmLabel: 'Clear override',
    }))) return;
    setBusy(true);
    try { await clearPaymentOverride(field); await refresh(); }
    catch (err) { toast.error('Clear failed', err.response?.data?.message); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTestResult({ pending: true });
    try { setTestResult(await testPaymentConnection()); }
    catch (err) { setTestResult({ success: false, detail: err.response?.data?.detail || err.message }); }
  };

  return (
    <>
      <PageHeader
        title="Payment Settings"
        subtitle="Stripe Connect Express — config for transfers + webhook verification"
        action={<button className="btn btn-secondary" onClick={runTest} disabled={busy}><i className="bi bi-lightning-charge-fill" /> Test connection</button>}
      />

      {/* Mode card + summary */}
      <div className="ds-card" style={{ padding: 18, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ padding: '6px 14px', borderRadius: 8, background: mode.bg, color: mode.fg, fontWeight: 800, fontSize: 13, letterSpacing: '0.04em' }}>{mode.label}</span>
        <span style={{ fontSize: 13, color: '#5A6378' }}>{data.configured_count}/{data.total_fields} fields configured</span>
        <span style={{ fontSize: 12, color: '#8B94A8' }}>Webhook URL for Stripe Dashboard: <code style={{ background: '#F0F2F8', padding: '2px 8px', borderRadius: 4 }}>{data.webhook_url}</code></span>
      </div>

      {/* Test result banner */}
      {testResult && !testResult.pending && (
        <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 8, background: testResult.success ? '#DCFCE7' : '#FEE2E2', color: testResult.success ? '#166534' : '#991B1B', fontSize: 13, fontWeight: 600 }}>
          {testResult.success
            ? `✓ Connected in ${testResult.duration_ms}ms (${testResult.mode} mode). Balance: ${(testResult.available?.length ?? 0)} currencies.`
            : `✗ ${testResult.detail}`}
        </div>
      )}

      {savedMsg && (
        <div style={{ padding: '8px 12px', marginBottom: 14, borderRadius: 6, background: '#DCFCE7', color: '#166534', fontSize: 12, fontWeight: 600 }}>
          {savedMsg}
        </div>
      )}

      {/* Fields */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Object.entries(data.fields).map(([key, f]) => {
          const isEditing  = key in edits;
          const editValue  = edits[key] ?? '';
          const isShown    = shown[key];
          const sourceMeta = SOURCE_META[f.source];

          return (
            <div key={key} className="ds-card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700 }}>{f.label}</p>
                  <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 2 }}>{f.description}</p>
                </div>
                <span style={{ padding: '3px 9px', borderRadius: 6, background: sourceMeta.bg, color: sourceMeta.fg, fontSize: 11, fontWeight: 700 }}>{sourceMeta.label}</span>
              </div>

              {/* Current value (masked) */}
              <div style={{ padding: '10px 12px', background: '#FAFBFD', borderRadius: 8, fontFamily: 'ui-monospace, monospace', fontSize: 13, color: f.is_set ? '#0F1225' : '#8B94A8', marginBottom: 10, border: '1px solid #F0F2F8' }}>
                {f.is_set ? f.masked : 'not configured'}
              </div>

              {/* Edit row */}
              {isEditing ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type={isShown ? 'text' : 'password'}
                    className="form-input"
                    style={{ flex: 1, minWidth: 240, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                    placeholder={`New value (must start with ${f.allowed_prefixes.join(' or ')})`}
                    value={editValue}
                    onChange={(e) => setEdits((p) => ({ ...p, [key]: e.target.value }))}
                    autoFocus
                  />
                  <button className="btn btn-secondary btn-sm" onClick={() => setShown((p) => ({ ...p, [key]: !p[key] }))}>{isShown ? 'Hide' : 'Show'}</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEdits((p) => { const { [key]: _, ...rest } = p; return rest; }); setShown((p) => { const { [key]: _, ...rest } = p; return rest; }); }}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEdits((p) => ({ ...p, [key]: '' }))}>{f.is_set ? 'Replace' : 'Set value'}</button>
                  {f.source === 'db' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => revert(key)} disabled={busy} style={{ color: '#991B1B' }}>↺ Revert to .env</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save bar */}
      {dirtyFields.length > 0 && (
        <div style={{ position: 'sticky', bottom: 16, marginTop: 18, padding: '12px 18px', background: '#fff', border: '1px solid #6B2FD9', borderRadius: 10, boxShadow: '0 4px 14px rgba(107,47,217,0.18)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{dirtyFields.length} field(s) pending save</span>
          <button className="btn btn-secondary btn-sm" onClick={() => { setEdits({}); setShown({}); }} disabled={busy}>Cancel all</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      )}
      {confirmNode}
    </>
  );
}

/* ============================= Mail Settings ============================= */

const MAIL_FIELD_GROUPS = [
  {
    id: 'general',
    title: 'General',
    description: 'Active mailer + sender identity. Applies to every email PerkVora sends.',
    fields: ['mailer', 'from_address', 'from_name'],
  },
  {
    id: 'smtp',
    title: 'SMTP transport',
    description: 'Standard SMTP relay. Used when Active mailer = smtp.',
    fields: ['smtp_host', 'smtp_port', 'smtp_encryption', 'smtp_username', 'smtp_password'],
  },
  {
    id: 'msgraph',
    title: 'Microsoft Graph transport',
    description: 'OAuth 2.0 client-credentials mail-send through Azure AD / M365. Required: Azure app with Microsoft Graph → Mail.Send (Application). The sender mailbox must be a real user in the same tenant.',
    fields: ['msgraph_tenant_id', 'msgraph_client_id', 'msgraph_client_secret', 'msgraph_sender'],
  },
];

function MailSettingsTab() {
  const { data, loading, refresh } = useMailSettings();
  const { user: me } = useAuth();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [edits, setEdits]   = useState({});
  const [shown, setShown]   = useState({});
  const [busy, setBusy]     = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [testResult, setTestResult] = useState(null);

  if (loading) return <LoadingPage />;
  if (!data) return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon={<i className="bi bi-envelope" />} title="Mail settings endpoint unavailable" /></div></div>;

  const activeMailer = data.active_mailer || 'log';
  const dirtyFields  = Object.keys(edits);

  const save = async () => {
    if (dirtyFields.length === 0) return;
    setBusy(true); setSavedMsg('');
    try {
      const updates = dirtyFields.map((field) => ({ field, value: edits[field] ?? '' }));
      const res = await updateMailSettings(updates);
      if (res.errors && Object.keys(res.errors).length > 0) {
        const lines = Object.entries(res.errors).map(([k, v]) => `${k}: ${v}`).join(' · ');
        toast.error('Some fields failed', lines);
      }
      setEdits({}); setShown({});
      setSavedMsg(`✓ ${res.applied?.length || 0} field(s) saved.`);
      await refresh();
    } catch (err) {
      toast.error('Save failed', err.response?.data?.message);
    } finally { setBusy(false); }
  };

  const revert = async (field) => {
    if (!(await confirm({
      title: 'Clear database override?',
      message: `${field} will fall back to whatever .env defines (if anything).`,
      confirmLabel: 'Clear override',
    }))) return;
    setBusy(true);
    try { await clearMailOverride(field); await refresh(); }
    catch (err) { toast.error('Clear failed', err.response?.data?.message); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTestResult({ pending: true });
    try { setTestResult(await sendMailTest(testRecipient || undefined)); }
    catch (err) { setTestResult({ success: false, detail: err.response?.data?.detail || err.message }); }
  };

  return (
    <>
      <PageHeader
        title="Mail Settings"
        subtitle={`Active mailer: ${activeMailer} · choose between SMTP, sendmail, log (dev), or Microsoft Graph (OAuth)`}
      />

      {/* Test panel */}
      <div className="ds-card" style={{ padding: 16, marginBottom: 18 }}>
        <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Send a test email</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="email" className="form-input" placeholder={`Defaults to your account (${me?.email})`} value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} style={{ flex: 1, minWidth: 240, fontSize: 13 }} />
          <button className="btn btn-secondary btn-sm" onClick={runTest} disabled={busy}><i className="bi bi-lightning-charge-fill" /> Send test</button>
        </div>
        {testResult && !testResult.pending && (
          <div style={{ padding: '8px 12px', marginTop: 10, borderRadius: 6, background: testResult.success ? '#DCFCE7' : '#FEE2E2', color: testResult.success ? '#166534' : '#991B1B', fontSize: 12, fontWeight: 600 }}>
            {testResult.success
              ? `✓ Sent via ${testResult.mailer} in ${testResult.duration_ms}ms → ${testResult.recipient}. ${testResult.detail || ''}`
              : `✗ ${testResult.detail}`}
          </div>
        )}
      </div>

      {savedMsg && (
        <div style={{ padding: '8px 12px', marginBottom: 14, borderRadius: 6, background: '#DCFCE7', color: '#166534', fontSize: 12, fontWeight: 600 }}>
          {savedMsg}
        </div>
      )}

      {/* Field groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {MAIL_FIELD_GROUPS.map((grp) => {
          // For SMTP and MsGraph groups, dim if a different mailer is active.
          const isInactive = (grp.id === 'smtp' && activeMailer !== 'smtp') || (grp.id === 'msgraph' && activeMailer !== 'msgraph');
          return (
            <section key={grp.id} className="ds-card" style={{ padding: 18, opacity: isInactive ? 0.65 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{grp.title}</p>
                  <p style={{ fontSize: 11.5, color: '#8B94A8', marginTop: 2 }}>{grp.description}</p>
                </div>
                {isInactive && <span style={{ padding: '2px 8px', borderRadius: 6, background: '#F0F2F8', color: '#5A6378', fontSize: 11, fontWeight: 700 }}>inactive</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                {grp.fields.map((name) => (
                  <MailFieldEditor
                    key={name}
                    name={name}
                    field={data.fields[name]}
                    isShown={shown[name]}
                    isEditing={name in edits}
                    editValue={edits[name] ?? ''}
                    onShow={() => setShown((p) => ({ ...p, [name]: !p[name] }))}
                    onStartEdit={() => setEdits((p) => ({ ...p, [name]: field('display', data.fields[name]) }))}
                    onChange={(v) => setEdits((p) => ({ ...p, [name]: v }))}
                    onCancel={() => { setEdits((p) => { const { [name]: _, ...rest } = p; return rest; }); setShown((p) => { const { [name]: _, ...rest } = p; return rest; }); }}
                    onRevert={() => revert(name)}
                    busy={busy}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Save bar */}
      {dirtyFields.length > 0 && (
        <div style={{ position: 'sticky', bottom: 16, marginTop: 18, padding: '12px 18px', background: '#fff', border: '1px solid #6B2FD9', borderRadius: 10, boxShadow: '0 4px 14px rgba(107,47,217,0.18)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{dirtyFields.length} field(s) pending save</span>
          <button className="btn btn-secondary btn-sm" onClick={() => { setEdits({}); setShown({}); }} disabled={busy}>Cancel all</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      )}
      {confirmNode}
    </>
  );
}

// Inline helper used by MailSettingsTab to pre-fill the edit box.
function field(prop, f) {
  if (!f) return '';
  if (prop === 'display' && f.type === 'secret') return ''; // never pre-fill secret edits
  return f.display ?? '';
}

function MailFieldEditor({ name, field, isShown, isEditing, editValue, onShow, onStartEdit, onChange, onCancel, onRevert, busy }) {
  if (!field) return null;
  const sourceMeta = SOURCE_META[field.source];
  const isSecret = field.type === 'secret';

  // Input element varies by type.
  const renderEditor = () => {
    if (field.type === 'choice') {
      return (
        <select className="form-input form-select" value={editValue} onChange={(e) => onChange(e.target.value)} style={{ fontSize: 13 }} autoFocus>
          <option value="">— select —</option>
          {(field.choices || []).filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }
    return (
      <input
        type={isSecret && !isShown ? 'password' : (field.type === 'email' ? 'email' : (field.type === 'port' ? 'number' : 'text'))}
        className="form-input"
        style={{ fontFamily: isSecret ? 'ui-monospace, monospace' : undefined, fontSize: 13 }}
        value={editValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isSecret ? '(leave empty to clear)' : ''}
        autoFocus
      />
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <p style={{ fontSize: 11.5, fontWeight: 700, color: '#5A6378', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{field.label}</p>
        <span style={{ padding: '1px 7px', borderRadius: 4, background: sourceMeta.bg, color: sourceMeta.fg, fontSize: 10.5, fontWeight: 700 }}>{sourceMeta.label}</span>
      </div>

      <div style={{ padding: '8px 10px', background: '#FAFBFD', borderRadius: 6, border: '1px solid #F0F2F8', fontSize: 13, fontFamily: isSecret ? 'ui-monospace, monospace' : undefined, color: field.is_set ? '#0F1225' : '#8B94A8', marginBottom: 6 }}>
        {field.is_set ? field.display : 'not configured'}
      </div>

      {isEditing ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {renderEditor()}
          {isSecret && <button className="btn btn-secondary btn-sm" onClick={onShow}>{isShown ? 'Hide' : 'Show'}</button>}
          <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={onStartEdit}>{field.is_set ? 'Replace' : 'Set'}</button>
          {field.source === 'db' && (
            <button className="btn btn-secondary btn-sm" onClick={onRevert} disabled={busy} style={{ color: '#991B1B' }}>↺ Revert to .env</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================= Data Export ============================= */

const EXPORT_ENTITIES = [
  { key: 'users',           label: 'Users',           desc: 'All registered users across tenants' },
  { key: 'coupons',         label: 'Coupons',         desc: 'Every coupon with type, discount, dates' },
  { key: 'merchants',       label: 'Merchants',       desc: 'Brand directory with commission + status' },
  { key: 'redemptions',     label: 'Redemptions',     desc: 'Click-throughs that produced a redemption row' },
  { key: 'cashback_claims', label: 'Cashback Claims', desc: 'Claim ledger with status + amounts' },
];

function ExportTab() {
  const { toast } = useToast();
  const [busy, setBusy]     = useState(null);
  const [from, setFrom]     = useState('');
  const [to, setTo]         = useState('');

  const run = async (entity) => {
    setBusy(entity);
    try {
      const params = new URLSearchParams({ limit: 50000 });
      if (from) params.append('from', from);
      if (to)   params.append('to', to);
      const res = await api.get(`/admin/export/${entity}?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `perkvora-${entity}-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 429) toast.error('Export rate-limited', '3 exports per minute. Try again shortly.');
      else                              toast.error('Export failed', err.response?.data?.message);
    } finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader title="Data Export" subtitle="Stream CSVs of any entity. Throttled to 3 exports/minute." />

      <div className="ds-panel" style={{ marginBottom: 16 }}>
        <div className="ds-panel-body">
          <p style={{ fontWeight: 700, marginBottom: 10 }}>Optional date range</p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#5A6378' }}>From</span>
              <input className="form-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#5A6378' }}>To</span>
              <input className="form-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            {(from || to) && <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(''); setTo(''); }}>Clear</button>}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {EXPORT_ENTITIES.map(e => (
          <div key={e.key} className="ds-card">
            <p style={{ fontWeight: 700, fontSize: 15 }}>{e.label}</p>
            <p style={{ fontSize: 13, color: '#5A6378', marginTop: 4 }}>{e.desc}</p>
            <button className="btn btn-primary btn-sm" disabled={busy === e.key} onClick={() => run(e.key)} style={{ marginTop: 12 }}>{busy === e.key ? 'Streaming…' : '↓ Download CSV'}</button>
          </div>
        ))}
      </div>
    </>
  );
}

/* ============================= Main export ============================= */

export default function AdminDashboard() {
  const [tab, setTab] = useState('overview');
  // Scroll the page back to the top when switching tabs. Without this, if
  // the previous tab had been scrolled down (e.g. long audit log), the
  // viewport stayed pinned at the bottom and the new tab appeared to "land"
  // on the footer instead of its own header.
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [tab]);
  return (
    <AdminLayout active={tab} setActive={setTab}>
      {tab === 'overview'    && <OverviewTab />}
      {tab === 'coupons'     && <CouponsTab />}
      {tab === 'submissions' && <SubmissionsTab />}
      {tab === 'affiliate'   && <AffiliateFeedsTab />}
      {tab === 'merchants'   && <MerchantsTab />}
      {tab === 'cashback'    && <CashbackPoolsTab />}
      {tab === 'apps'        && <AppClientsTab />}
      {tab === 'users'       && <UsersTab />}
      {tab === 'fraud'       && <FraudTab />}
      {tab === 'audit'       && <AuditLogTab />}
      {tab === 'roles'       && <RolesTab />}
      {tab === 'webhooks'    && <WebhooksTab />}
      {tab === 'flags'       && <FeatureFlagsTab />}
      {tab === 'broadcasts'  && <BroadcastsTab />}
      {tab === 'funnel'      && <FunnelTab />}
      {tab === 'email'       && <EmailHealthTab />}
      {tab === 'cohorts'     && <CohortsTab />}
      {tab === 'revenue'     && <RevenueTab />}
      {tab === 'tiers'       && <TierLifecycleTab />}
      {tab === 'banners'     && <BannersTab />}
      {tab === 'blog'        && <BlogTab />}
      {tab === 'settings'    && <SettingsTab />}
      {tab === 'payments'    && <PaymentSettingsTab />}
      {tab === 'mail'        && <MailSettingsTab />}
      {tab === 'export'      && <ExportTab />}
      {tab === 'health'      && <HealthTab />}
      {tab === 'roadmap'     && <FeatureRoadmap />}
    </AdminLayout>
  );
}
